import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PencilIcon, XIcon, CalendarIcon, ClockIcon, DocumentIcon, UserIcon, UsersIcon, CheckCircleIcon, ShieldCheckIcon, ChatIcon } from '@heroicons/react/solid';
import { toast } from "react-hot-toast";

import { fetchCandidateDetails, fetchHiringManagers, ScheduleInterview, UpdateCandidateStaus, UpdateInterview, loadcompetencies, UploadDocuments, UpdateHiringPackage, InsertHiringPackage, InsertApprovalLevel, SendForApproval, UpdateCandidate, CancelInterview, EvaluationForCandidate, HiringRequestStatusHistory, SaveHiringRequestStatus } from '../services/apiService';
import NationalityList from './NationalityList';
import nationalities from './nationalities';
import { getIsDepartmentRestrictedToken, getToken, getUserFromToken, getUserIDFromToken, getCompanyCodeFromToken } from '../services/authService';

import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select';
import ScheduleCheckModal from './ScheduleCheckModal';
import { cancelTeamsMeetingEvent, createTeamsMeetingEvent } from '../services/graphUtils';
import html2pdf from 'html2pdf.js';

import { Client } from "@microsoft/microsoft-graph-client";
import { useMsal } from '@azure/msal-react';
import { sendCandidateFinalStatusWithTemplate } from '../services/sendEmail';
import { SendCandidateStatusEmailAsync, SendDirectEvaluationEmailAsync, sendInterviewInvitationEmailAsync, SendApprovalWorkflowEmailAsync } from '../services/sendMailSMTP';
import { loginRequest } from '../services/msalConfig';
import CandidateApprovalFlow from './CandidateApprovalFlow';

// Theme colors
const PRIMARY_COLOR = 'rgb(18, 76, 114)';
const PRIMARY_LIGHT = 'rgba(18, 76, 114, 0.1)';
const PRIMARY_MEDIUM = 'rgba(18, 76, 114, 0.3)';
const PRIMARY_DARK = 'rgba(18, 76, 114, 0.8)';
const BG_GRADIENT = 'linear-gradient(135deg, rgba(18, 76, 114, 0.05) 0%, rgba(18, 76, 114, 0.02) 100%)';
const SHADOW_COLOR = 'rgba(18, 76, 114, 0.1)';
const SUCCESS_COLOR = '#22c55e';
const WARNING_COLOR = '#f59e0b';
const ERROR_COLOR = '#ef4444';
const INFO_COLOR = '#3b82f6';

// ==========================================
// ROLE-BASED PERMISSIONS CONFIGURATION
// ==========================================

const ROLE_PERMISSIONS = {
  'Admin': {
    canEditCandidate: true,
    canSendForInitialApproval: true,
    canInitialApproval: true,
    canShortlistForInterview: true,
    canScheduleInterview: true,
    canAddEvaluation: true,
    canShortlistAfterEvaluation: true,
    canStartHiringProcess: true,
    canSendForApproval: true,
    canUploadDocuments: true,
    canApprove: true,
    canViewAllTabs: true,
    canGenerateOfferLetter: true,
  },
  'HR Specialist': {
    canEditCandidate: true,
    canSendForInitialApproval: true,
    canInitialApproval: false,
    canShortlistForInterview: true,
    canScheduleInterview: true,
    canAddEvaluation: true,
    canShortlistAfterEvaluation: true,
    canStartHiringProcess: true,
    canSendForApproval: true,
    canUploadDocuments: true,
    canApprove: false,
    canViewAllTabs: true,
    canGenerateOfferLetter: true,
    canRejectApplied: true,
  },
  'HR Manager': {
    canEditCandidate: true,
    canSendForInitialApproval: true,
    canInitialApproval: false,
    canShortlistForInterview: true,
    canScheduleInterview: true,
    canAddEvaluation: true,
    canShortlistAfterEvaluation: true,
    canStartHiringProcess: true,
    canSendForApproval: true,
    canUploadDocuments: true,
    canApprove: true,
    canViewAllTabs: true,
    canGenerateOfferLetter: true,
    canRejectApplied: true,
  },
  'HR Manager 2': {
    canEditCandidate: true,
    canSendForInitialApproval: true,
    canInitialApproval: false,
    canShortlistForInterview: true,
    canScheduleInterview: true,
    canAddEvaluation: true,
    canShortlistAfterEvaluation: true,
    canStartHiringProcess: true,
    canSendForApproval: true,
    canUploadDocuments: true,
    canApprove: true,
    canViewAllTabs: true,
    canGenerateOfferLetter: true,
  },
  'HR Director': {
    canEditCandidate: false,
    canSendForInitialApproval: false,
    canInitialApproval: false,
    canShortlistForInterview: false,
    canScheduleInterview: false,
    canAddEvaluation: true,
    canShortlistAfterEvaluation: false,
    canStartHiringProcess: false,
    canSendForApproval: false,
    canUploadDocuments: false,
    canApprove: true,
    canViewAllTabs: true,
    canGenerateOfferLetter: false,
  },
  'Business Owner': {
    canEditCandidate: false,
    canSendForInitialApproval: false,
    canInitialApproval: true,
    canShortlistForInterview: false,
    canScheduleInterview: false,
    canAddEvaluation: true,
    canShortlistAfterEvaluation: false,
    canStartHiringProcess: false,
    canSendForApproval: false,
    canUploadDocuments: false,
    canApprove: true,
    canViewAllTabs: true,
    canGenerateOfferLetter: false,
  },
  'Finance Manager': {
    canEditCandidate: false,
    canSendForInitialApproval: false,
    canInitialApproval: true,
    canShortlistForInterview: false,
    canScheduleInterview: false,
    canAddEvaluation: false,
    canShortlistAfterEvaluation: false,
    canStartHiringProcess: false,
    canSendForApproval: false,
    canUploadDocuments: false,
    canApprove: true,
    canViewAllTabs: true,
    canGenerateOfferLetter: false,
  },
  'Chief Finance': {
    canEditCandidate: false,
    canSendForInitialApproval: false,
    canInitialApproval: false,
    canShortlistForInterview: false,
    canScheduleInterview: false,
    canAddEvaluation: false,
    canShortlistAfterEvaluation: false,
    canStartHiringProcess: false,
    canSendForApproval: false,
    canUploadDocuments: false,
    canApprove: true,
    canViewAllTabs: true,
    canGenerateOfferLetter: false,
  },
  'CEO': {
    canEditCandidate: false,
    canSendForInitialApproval: false,
    canInitialApproval: false,
    canShortlistForInterview: false,
    canScheduleInterview: false,
    canAddEvaluation: false,
    canShortlistAfterEvaluation: false,
    canStartHiringProcess: false,
    canSendForApproval: false,
    canUploadDocuments: false,
    canApprove: true,
    canViewAllTabs: true,
    canGenerateOfferLetter: false,
  },
  'Interview Board': {
    canEditCandidate: false,
    canSendForInitialApproval: false,
    canInitialApproval: false,
    canShortlistForInterview: false,
    canScheduleInterview: false,
    canAddEvaluation: true,
    canShortlistAfterEvaluation: false,
    canStartHiringProcess: false,
    canSendForApproval: false,
    canUploadDocuments: false,
    canApprove: false,
    canViewAllTabs: false,
    canGenerateOfferLetter: false,
  },
};

// Statuses that block editing
const NON_EDITABLE_STATUSES = [
  'Under Hiring Process',
  'Hired',
  'Approved',
  'Send For Approval',
  'Rejected',
];

const CandidateDetails = () => {
  const location = useLocation();
  // Recover id from URL query param if navigation state is lost (e.g. after refresh or mobile-view reload).
  const id = location.state?.id || new URLSearchParams(location.search).get('id');
  const navigate = useNavigate();
  const { instance } = useMsal();

  const [userRole, setUserRole] = useState(null);
  const [candidateDetails, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setStatusLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showJoiningModal, setShowJoiningModal] = useState(false);
  const [joiningDateValue, setJoiningDateValue] = useState("");
  const [showJoinedConfirm, setShowJoinedConfirm] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isDocumentModalOpen, setDocumentIsModalOpen] = useState(false);
  const [isStatusModalOpen, setStatuIsModalOpen] = useState(false);
  const [isStatusModalOpenShortList, setStatuIsModalOpenShortList] = useState(false);
  const [isStatusModalOpenInitial, setStatuIsModalOpenInitial] = useState(false);
  const [comments, setComments] = useState('');
  const [processType, setProcessType] = useState('');
  const [customProcessType, setCustomProcessType] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [showMoreInfoPopup, setShowMoreInfoPopup] = useState(false);
  const [moreInfoTab, setMoreInfoTab] = useState('personal');
  const [hrAuditTrail, setHrAuditTrail] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    date_of_birth: "",
    gender: "",
    nationality: "",
    is_citizen: false,
    is_transfer_ownership: false,
    is_new_visa: false,
    source_of_cv: "",
    status: "",
    highest_degree: "",
    major: "",
    institute_name: "",
    completion_year: "",
    descriptions: "",
    company_name: "",
    reason_for_leaving: "",
    experience_years: "",
    experience_years_at_ksa: "",
    last_job_title: "",
    last_salary: "",
    candidate_id: 0,
    Documents: {
      fileName: "",
      fileBase64: "",
      size: 0,
      mimeType: "",
      reference_id: "0",
      process_type: "",
      uploaded_by: "",
      uploaded_at: "",
      IsActive: true,
    },
  });
  const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);
  const [interviewDate, setInterviewDate] = useState(null);
  const [interviewType, setInterviewType] = useState('');
  const [interviewTimeFrom, setInterviewTimeFrom] = useState('');
  const [interviewTimeTo, setInterviewTimeTo] = useState('');
  const [interviewBoard, setInterviewBoard] = useState([]);
  const [interviewNotes, setInterviewNotes] = useState('');
  const [interviewBoardUsers, setInterviewBoardUsers] = useState([]);
  const [editInterview, setEditInterview] = useState(null);
  const [cancelInterview, setCancelInterview] = useState(null);
  const [showHiringModal, setShowHiringModal] = useState(false);
  const [finalStatus, setFinalStatus] = useState('');
  const [FinalApprovalStatus, setFinalApprovalStatus] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [competencies, setCompetencies] = useState([]);
  const [showHiringPackageModal, setHiringPackageModal] = useState(false);
  const [salaryDetails, setSalaryDetails] = useState({
    basicSalary: '',
    accommodation: '',
    transportation: '',
    additionalComments: '',
  });
  const [showSendForApprovalModal, setshowSendForApprovalModal] = useState(false);
  const [hiringManagers, setHiringManagers] = useState([]);
  const [isSchedulePopupOpen, setIsSchedulePopupOpen] = useState(false);
  const [showApprovalModal, setshowApprovalModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [offerLetterHtml, setOfferLetterHtml] = useState('');
  const [showOfferLetterModalOpen, setShowOfferLetterModalOpen] = useState(false);

  // ==========================================
  // PERMISSIONS HELPER FUNCTION
  // ==========================================
  const getUserPermissions = () => {
    const roleName = userRole?.role || '';
    const userId = getUserIDFromToken();
    const isHiringManagerForRequest = candidateDetails?.hiringRequests?.hiring_manager?.toString() === userId?.toString();
    const isRecruiterForRequest = candidateDetails?.hiringRequests?.recruiter_id?.toString() === userId?.toString();

    const basePermissions = ROLE_PERMISSIONS[roleName] || {
      canEditCandidate: false,
      canSendForInitialApproval: false,
      canInitialApproval: false,
      canShortlistForInterview: false,
      canScheduleInterview: false,
      canAddEvaluation: false,
      canShortlistAfterEvaluation: false,
      canStartHiringProcess: false,
      canSendForApproval: false,
      canUploadDocuments: false,
      canApprove: false,
      canViewAllTabs: false,
      canGenerateOfferLetter: false,
    };

    const isCurrentApprover = candidateDetails?.candidate?.approval_level_name === roleName ||
      (candidateDetails?.candidate?.approval_level_order === 1 && isHiringManagerForRequest);

    const currentStatus = candidateDetails?.candidate?.status || '';
    const isStatusEditable = !NON_EDITABLE_STATUSES.includes(currentStatus) &&
      !currentStatus.startsWith('Approved Level');

    return {
      ...basePermissions,
      isCurrentApprover,
      isHiringManagerForRequest,
      isRecruiterForRequest,
      isStatusEditable,
      roleName,
    };
  };
useEffect(() => {
  if (isEditModalOpen && formData.nationality) {
    setSearchTerm(formData.nationality);
  }
}, [isEditModalOpen, formData.nationality]);

  useEffect(() => {
    if (candidateDetails && userRole) {
      const userPerms = getUserPermissions();
      setPermissions(userPerms);
    }
  }, [candidateDetails, userRole]);

  useEffect(() => {
    getCandidateDetails();
  }, [id]);

  useEffect(() => {
    if (formData.nationality) {
      setSearchTerm(formData.nationality);
    }
  }, [formData.nationality]);

  useEffect(() => {
    loadInterviewBoardUsers();
    loadHiringManagers();
    loadcompetency();
    const token = getToken();
    setUserRole(token);
  }, []);

  const getCandidateDetails = async () => {
    try {
      setLoading(true);
      const candidateData = await fetchCandidateDetails(id);
      setCandidate(candidateData);

      setFormData((prev) => ({
        ...prev,
        full_name: candidateData?.candidate?.full_name || "",
        email: candidateData?.candidate?.email || "",
        phone_number: candidateData?.candidate?.phone_number || "",
        date_of_birth: candidateData?.candidate?.date_of_birth || "",
        gender: candidateData?.candidate?.gender || "",
        source_of_cv: candidateData?.candidate?.source_of_cv || "",
        nationality: candidateData?.candidate?.nationality || "",
        is_citizen: candidateData?.candidate?.is_citizen || false,
        is_transfer_ownership: candidateData?.candidate?.is_transfer_ownership || false,
        is_new_visa: candidateData?.candidate?.is_new_visa || false,
        application_date: candidateData?.candidate?.application_date || "",
        company_name: candidateData?.candidate?.company_name || "",
        experience_years: candidateData?.candidate?.experience_years || "",
        experience_years_at_ksa: candidateData?.candidate?.experience_years_at_ksa || "",
        last_job_title: candidateData?.candidate?.last_job_title || "",
        last_salary: candidateData?.candidate?.last_salary || "",
        expected_salary: candidateData?.candidate?.expected_salary || "",
        status: candidateData?.candidate?.status || 0,
        highest_degree: candidateData?.candidate?.highest_degree || "",
        major: candidateData?.candidate?.major || "",
        institute_name: candidateData?.candidate?.institute_name || "",
        completion_year: candidateData?.candidate?.completion_year || "",
        reason_for_leaving: candidateData?.candidate?.reason_for_leaving || "",
        descriptions: candidateData?.candidate?.descriptions || "",
        candidate_id: candidateData?.candidate?.candidate_id || 0,
      }));
    } catch (err) {
      toast.error('Failed to load candidate details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const loadHiringManagers = async () => {
    try {
      const managers = await fetchHiringManagers();
      setHiringManagers(managers);
    } catch (err) {
      console.error("Failed to load hiring managers:", err);
    }
  };

  const loadcompetency = async () => {
    try {
      const managers = await loadcompetencies();
      setCompetencies(managers);
    } catch (err) {
      console.error("Failed to load interview board users:", err);
    }
  };

  const loadInterviewBoardUsers = async () => {
    try {
      const managers = await fetchHiringManagers();
      setInterviewBoardUsers(managers);
    } catch (err) {
      console.error("Failed to load interview board users:", err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateToInput = (date) => {
    if (!date) return '';
    const dateObj = new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatLocalDateTime = (date) => {
    const pad = (n) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const formatLocalDateTime1 = (date) => {
    const validDate = new Date(date);
    if (isNaN(validDate.getTime())) {
      console.error("Invalid Date:", date);
      return 'Invalid Date';
    }
    return validDate.toLocaleString();
  };

  const formatTimeOnly = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return date.toTimeString().slice(0, 5);
  };

  function combineDateAndTime(date, timeStr) {
    const [hours, minutes] = timeStr.split(":").map(Number);
    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
  }

  const cleanNumericFields = (candidate) => {
    const fields = ["experience_years", "experience_years_at_ksa", "last_salary", "expected_salary"];
    const cleaned = { ...candidate };

    fields.forEach((field) => {
      const value = cleaned[field];
      if (value === "" || value == null || isNaN(value)) {
        cleaned[field] = null;
      } else {
        cleaned[field] = Number(value);
      }
    });

    return cleaned;
  };

  const filteredNationalities = nationalities.filter((n) =>
    n.label.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  const interviewBoardOptions = interviewBoardUsers.map((user) => ({
    value: user.user_id,
    label: `${user.first_name} ${user.last_name}`,
    users: user,
  }));

  const handleInterviweStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleEvaluationModalOpen = async () => {
    const candidateData = await fetchCandidateDetails(id);
    navigate(`/evaluation/`, {
      state: {
        candidateDetails: candidateData,
        competencies,
      },
    });
  };

  const handleInterviewModalOpen = () => {
    const { email, phone_number } = candidateDetails.candidate;

    if (!email || !phone_number) {
      toast.error("Email and phone number are required before scheduling an interview.");
      return;
    }

    setIsInterviewModalOpen(true);
  };

  const handleInterviewModalClose = () => {
    setIsInterviewModalOpen(false);
    setInterviewDate(null);
    setInterviewTimeFrom('');
    setInterviewType('');
    setInterviewTimeTo('');
    setInterviewBoard([]);
    setInterviewNotes('');
  };

  const handleInterviewBoardChange = (selectedOptions) => {
    setInterviewBoard(selectedOptions);
  };

const handleSelectNationality = (n) => {
  setFormData((prevFormData) => ({
    ...prevFormData,
    nationality: n.label,
  }));
  setSearchTerm(n.label);
  setIsDropdownVisible(false);
};

  const handleTabChange = (tab) => setActiveTab(tab);

  const handleOpenModal = (doc) => {
    // For Azure storage, use blobUrl. For Base64, use fileBase64.
    const isAzure = doc.storageType === 'Azure' || doc.StorageType === 'Azure';
    const previewDoc = {
      ...doc,
      previewUrl: isAzure && (doc.blobUrl || doc.BlobUrl)
        ? (doc.blobUrl || doc.BlobUrl)
        : doc.fileBase64 ? `data:${doc.mimeType};base64,${doc.fileBase64}` : null,
      isAzure,
    };
    setSelectedDocument(previewDoc);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDocument(null);
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleEditClose = () => {
    setIsEditModalOpen(false);
  };

  const handleStatusModelClose = () => {
    setStatuIsModalOpen(false);
  };

  const handleStatusModelCloseshortList = () => {
    setStatuIsModalOpenShortList(false);
  };

  const handleStatusModelClosesinitial = () => {
    setStatuIsModalOpenInitial(false);
  };

  const handleHiringModalClose = () => {
    setShowHiringModal(false);
    setFinalStatus('');
    setComments('');
  };

  const handleHiringModalOpen = () => {
    setShowHiringModal(true);
  };

  const handleSendforApprovalModalClose = () => {
    setshowSendForApprovalModal(false);
  };

  const handleSendforApprovalModalOpen = () => {
    setshowSendForApprovalModal(true);
  };

  const handleApprovalModalClose = () => {
    setshowApprovalModal(false);
  };

  const handleApprovalModalOpen = () => {
    setshowApprovalModal(true);
  };

  const handleHiringPackageModalClose = () => setHiringPackageModal(false);

  const openHiringPackageModal = () => {
    const existing = candidateDetails?.salaryDetails;

    if (existing && existing.basicSalary) {
      setIsEditMode(true);
      setSalaryDetails({
        basicSalary: existing.basicSalary || '',
        accommodation: existing.accommodation || '',
        transportation: existing.transportation || '',
        additionalComments: existing.additionalComments || '',
      });
    } else {
      setIsEditMode(false);
      setSalaryDetails({
        basicSalary: '',
        accommodation: '',
        transportation: '',
        additionalComments: '',
      });
    }

    setHiringPackageModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const exclusiveFields = ['is_citizen', 'is_transfer_ownership', 'is_new_visa'];

    if (type === 'checkbox' && exclusiveFields.includes(name)) {
      setFormData((prev) => {
        const updated = { ...prev };
        exclusiveFields.forEach((field) => {
          updated[field] = false;
        });
        updated[name] = checked;
        return updated;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const userName = getUserFromToken();

    const validTypes = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg"
    ];

    if (file) {
      if (!validTypes.includes(file.type)) {
        toast.error("Invalid file type. Please upload PDF, PNG, JPG.");
        e.target.value = null;
        return;
      }

      if (file.size > 5_000_000) {
        toast.error("File size exceeds 5MB. Please upload a smaller file.");
        e.target.value = null;
        return;
      }

      const reader = new FileReader();

      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];

        setFormData((prev) => ({
          ...prev,
          Documents: {
            ...prev.Documents,
            fileName: file.name,
            fileBase64: base64String,
            size: file.size,
            mimeType: file.type,
            reference_id: candidateDetails.candidate.referenceNumber,
            process_type: "Resume",
            uploaded_by: userName,
            uploaded_at: new Date().toISOString(),
            IsActive: true,
          },
        }));
      };

      reader.readAsDataURL(file);
    } else {
      toast.error("Please select a file.");
    }
  };

  const handleDocFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadError('');
      const userName = getUserFromToken();

      if (!file) {
        toast.error("No file selected.");
        return;
      }

      const validTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
      if (!validTypes.includes(file.type)) {
        toast.error("Invalid file type. Only PDF or image files are allowed.");
        e.target.value = null;
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size exceeds 5MB. Please upload a smaller file.");
        e.target.value = null;
        return;
      }

      setDocFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];

        setFormData((prev) => ({
          ...prev,
          Documents: {
            fileName: file.name,
            fileBase64: base64String,
            Size: file.size,
            mimeType: file.type,
            reference_id: candidateDetails.candidate.referenceNumber,
            process_type: processType,
            uploaded_by: userName,
            uploaded_at: new Date().toISOString(),
            IsActive: true,
            other_Process_type: customProcessType
          }
        }));
      };

      reader.readAsDataURL(file);
    }
  };

  const handleProcessTypeChange = (e) => {
    setProcessType(e.target.value);
    if (e.target.value !== 'Other') {
      setCustomProcessType('');
    }
  };

  const handleCustomProcessTypeChange = (e) => {
    setCustomProcessType(e.target.value);
  };

  const closeDocModal = () => {
    setDocumentIsModalOpen(false);
    setProcessType('');
    setCustomProcessType('');
    setDocFile(null);
  };

 const handleScheduleInterview = async () => {
  // Only login for online interviews
  if (interviewType === "Online") {
    await instance.loginPopup(loginRequest);
  }

  const interviewStart = combineDateAndTime(interviewDate, interviewTimeFrom);
  const interviewEnd = combineDateAndTime(interviewDate, interviewTimeTo);

  try {
    setStatusUpdating(true);
    const userName = getUserFromToken();

    const boardEmails = [
      ...interviewBoard.map((member) => member.users.email),
    ];
    const candidateEmail = candidateDetails.candidate.email;
    
    // Make sure recipients is properly defined as an array
    const allRecipients = [candidateEmail, ...boardEmails];
    
    console.log("Recipients:", allRecipients); // Debug log

    // Handle Interview Cancelation
    if (cancelInterview) {
      // Send cancellation email first (optional, can be after cancellation)
      const cancellationDetails = {
        recipients: allRecipients,
        interviewDate: formatLocalDateTime(interviewDate).split(" ")[0],
        startTime: formatLocalDateTime(interviewStart).split(" ")[1],
        endTime: formatLocalDateTime(interviewEnd).split(" ")[1],
        interviewType: interviewType,
        candidateName: candidateDetails.candidate.full_name,
        position: candidateDetails.hiringRequests?.position || candidateDetails.candidate.last_job_title,
        isCancellation: true,
        boardMembers: interviewBoard.map(member => `${member.users.first_name} ${member.users.last_name}`).join(", "),
        interviewNotes: interviewNotes || "No additional notes",
        location: interviewType === "Online" ? "Microsoft Teams" : (candidateDetails.candidate.location || "Office Location"),
      };
      
      // Cancel Teams meeting if it exists
      if (cancelInterview.microsoft_event_id) {
        const result = await cancelTeamsMeetingEvent({
          eventId: cancelInterview.microsoft_event_id,
          instance,
        });

        if (!result.success) {
          console.error("Cancel meeting error:", result.message);
        }
      }

      const cancelPayload = {
        interview_id: cancelInterview.interview_id,
        candidate_id: candidateDetails.candidate.candidate_id,
        status: "Interview Cancelled",
        cancelled_by: userName,
      };

      const cancelResponse = await CancelInterview(cancelPayload);

      if ([400, 500].includes(Number(cancelResponse.statusCode))) {
        toast.error("Failed to cancel the interview.", { position: "top-right" });
      } else {
        toast.success("Interview cancelled successfully!");
        
        // Send cancellation email
        try {
          await sendInterviewInvitationEmailAsync(cancellationDetails);
          console.log("Cancellation email sent successfully");
        } catch (emailError) {
          console.error("Failed to send cancellation email:", emailError);
          toast.warning("Interview cancelled but email notification failed to send.");
        }
        
        setCandidate(prev => {
          const updatedInterviews = prev.interviews.map(i =>
            i.interview_id === cancelInterview.interview_id
              ? { ...i, status: "Interview Cancelled" }
              : i
          );

          return {
            ...prev,
            candidate: {
              ...prev.candidate,
              status: "Interview Scheduled",
            },
            interviews: updatedInterviews,
          };
        });

        handleInterviewModalClose();
        setCancelInterview(null);
        setStatusUpdating(false);
      }

      return;
    }

    // Normal Scheduling/Rescheduling Flow
    let meetingUrl = null;
    let eventId = null;
    let meetingDetails = null;

    // Only create Teams meeting for Online interviews
    if (interviewType === "Online") {
      const meetingResult = await createTeamsMeetingEvent({
        subject: "Candidate Interview",
        startDateTime: new Date(interviewStart).toISOString(),
        endDateTime: new Date(interviewEnd).toISOString(),
        attendees: boardEmails,
        instance,
      });

      meetingUrl = meetingResult.meetingUrl;
      eventId = meetingResult.eventId;

      meetingDetails = {
        meetingUrl: meetingResult.meetingUrl,
        meetingId: meetingResult.meetingId,
        passcode: meetingResult.passcode,
      };
    }

    const interviewDetails = {
      candidate_id: candidateDetails.candidate.candidate_id,
      request_id: candidateDetails.candidate.request_id,
      interview_date: formatLocalDateTime(interviewDate).split(" ")[0],
      interview_time_from: formatLocalDateTime(interviewStart),
      interview_time_to: formatLocalDateTime(interviewEnd),
      interviewType: interviewType,
      interview_notes: interviewNotes || "",
      status: editInterview ? "Interview Rescheduled" : "Interview Scheduled",
      scheduled_by: userName,
      meeting_url: meetingUrl,
      microsoft_event_id: eventId,
      interviewBoardMembers: interviewBoard.map((member) => ({
        user_id: member.value,
        users: member.users,
      })),
    };

    if (editInterview?.interview_id) {
      interviewDetails.interview_id = editInterview.interview_id;
    }

    const response = editInterview
      ? await UpdateInterview(interviewDetails)
      : await ScheduleInterview(interviewDetails);

    if ([400, 500].includes(Number(response.statusCode))) {
      toast.error("Failed to schedule the interview.", { position: "top-right" });
    } else if (response.statusCode === 409) {
      toast.error("An interview is already scheduled or completed.", { position: "top-right" });
    } else if (response.statusCode === 609) {
      toast.error("One or more board members have a time conflict.");
    } else {
      toast.success(editInterview ? "Interview rescheduled successfully!" : "Interview scheduled successfully!");

      // Prepare email details for both online and offline interviews
      const emailDetails = {
        recipients: allRecipients,
        interviewDate: formatLocalDateTime(interviewDate).split(" ")[0],
        startTime: formatLocalDateTime(interviewStart).split(" ")[1],
        endTime: formatLocalDateTime(interviewEnd).split(" ")[1],
        interviewType: interviewType,
        location: interviewType === "Online" 
          ? "Microsoft Teams (Virtual Interview)" 
          : (candidateDetails.candidate.location || "Please contact HR for venue details"),
        interviewNotes: interviewNotes || "No additional notes",
        boardMembers: interviewBoard.map(member => `${member.users.first_name} ${member.users.last_name}`).join(", "),
        candidateName: candidateDetails.candidate.full_name,
        position: candidateDetails.hiringRequests?.position || candidateDetails.candidate.last_job_title,
        isReschedule: !!editInterview,
        isCancellation: false,
        meetingUrl: meetingUrl || null,
        meetingId: meetingDetails?.meetingId || null,
        passcode: meetingDetails?.passcode || null,
      };

      console.log("Sending email with details:", emailDetails);

      // Send email invitation for both online and offline interviews
      try {
        await sendInterviewInvitationEmailAsync(emailDetails);
        console.log("Email invitation sent successfully");
      } catch (emailError) {
        console.error("Failed to send email invitation:", emailError);
        toast.warning("Interview scheduled but email notification failed to send.");
      }

      setCandidate(prev => {
        let updatedInterviews;
        if (editInterview) {
          updatedInterviews = prev.interviews.map(i =>
            i.interview_id === interviewDetails.interview_id ? interviewDetails : i
          );
        } else {
          const existingInterviews = Array.isArray(prev.interviews) ? prev.interviews : [];
          updatedInterviews = [...existingInterviews, interviewDetails];
        }

        return {
          ...prev,
          candidate: {
            ...prev.candidate,
            status: "Interview Scheduled",
          },
          interviews: updatedInterviews,
        };
      });

      handleInterviewModalClose();
      setEditInterview(null);
      setStatusUpdating(false);
    }
  } catch (error) {
    console.error("Error during scheduling:", error);
    toast.error("An unexpected error occurred.");
  } finally {
    setStatusUpdating(false);
  }
};

  const handleStatusClick = async (status) => {
    if (statusUpdating) return;

    try {
      if (!comments && status !== 'Ready For Evaluation') {
        toast.error("Please provide comments.");
        return;
      }

      setStatusUpdating(true);
      const userName = getUserFromToken();
      candidateDetails.candidate.updated_by = userName;
      candidateDetails.candidate.oldstatus = candidateDetails.candidate.status;
      
      if (status === "Ready For Evaluation") {
        candidateDetails.candidate.status = "Ready For Evaluation";
      } else if (status === "Rejected" || status === "Rejected By Hiring Manager" || status === "Rejected By Recruiter") {
        candidateDetails.candidate.status = "Rejected";
      } else {
        candidateDetails.candidate.status = status;
      }

      const hiringManageremail = hiringManagers.find(manager => Number(manager.user_id) === Number(candidateDetails.hiringRequests.hiring_manager))?.email;
      candidateDetails.candidate.SendapprovalNotes = comments;
      candidateDetails.candidate.hiringManageremail = hiringManageremail;

      const savedRequest = await UpdateCandidateStaus(candidateDetails.candidate);

      if (savedRequest.statusCode === false) {
        toast.error("Failed to update the candidate.");
      } else {
        toast.success("Candidate status updated successfully!");
        setCandidate(prev => ({
          ...prev,
          status: status
        }));

        let candidateStatusRequest = null;

        if (status === "Approved By Hiring Manager" || status === "Rejected By Hiring Manager") {
          const recruiterEmial = hiringManagers.find(manager => Number(manager.user_id) === Number(candidateDetails.hiringRequests.recruiter_id))?.email;

          const formattedHiringRequest = {
            requestId: candidateDetails.hiringRequests.request_id,
            position: candidateDetails.hiringRequests.position,
            requestType: candidateDetails.hiringRequests.request_type,
          };
          const formattedCandidates = [candidateDetails.candidate].map(c => ({
            full_name: c.full_name,
            email: c.email,
            phone_number: c.phone_number || "",
            last_job_title: c.last_job_title || "Not available"
          }));

          candidateStatusRequest = {
            recipientName: "Recruiter",
            recipientEmail: recruiterEmial,
            isApprovalRequest: false,
            isRecruiter: true,
            status: status,
            notes: comments,
            hiringRequest: formattedHiringRequest,
            candidates: formattedCandidates,
          };
        } else if (status === "Ready For Evaluation") {
          const hiringManageremail = hiringManagers.find(manager => Number(manager.user_id) === Number(candidateDetails.hiringRequests.hiring_manager))?.email;

          const formattedHiringRequest = {
            requestId: candidateDetails.hiringRequests.request_id,
            position: candidateDetails.hiringRequests.position,
            requestType: candidateDetails.hiringRequests.request_type,
          };
          const formattedCandidates = [candidateDetails.candidate].map(c => ({
            full_name: c.full_name,
            email: c.email,
            phone_number: c.phone_number || "",
            last_job_title: c.last_job_title || "Not available"
          }));

          candidateStatusRequest = {
            recipientName: "Business Owner",
            recipientEmail: hiringManageremail,
            isApprovalRequest: false,
            isRecruiter: false,
            status: status,
            notes: comments || "",
            hiringRequest: formattedHiringRequest,
            candidates: formattedCandidates,
          };
        } else if (status !== "Rejected By Recruiter") {
          const hiringManageremail = hiringManagers.find(manager => Number(manager.user_id) === Number(candidateDetails.hiringRequests.hiring_manager))?.email;

          const formattedHiringRequest = {
            requestId: candidateDetails.hiringRequests.request_id,
            position: candidateDetails.hiringRequests.position,
            requestType: candidateDetails.hiringRequests.request_type,
          };
          const formattedCandidates = [candidateDetails.candidate].map(c => ({
            full_name: c.full_name,
            email: c.email,
            phone_number: c.phone_number || "",
            last_job_title: c.last_job_title || "Not available"
          }));

          candidateStatusRequest = {
            recipientName: "Business Owner",
            recipientEmail: hiringManageremail,
            isApprovalRequest: status === "Send For Initial Approval",
            isRecruiter: false,
            status: status,
            notes: comments,
            hiringRequest: formattedHiringRequest,
            candidates: formattedCandidates,
          };
        }

        setStatusUpdating(false);
        setStatuIsModalOpen(false);
        setStatuIsModalOpenShortList(false);
        setStatuIsModalOpenInitial(false);

        if (candidateStatusRequest) {
          if (status === "Ready For Evaluation") {
            await SendDirectEvaluationEmailAsync(candidateStatusRequest);
          } else {
            await SendCandidateStatusEmailAsync(candidateStatusRequest);
          }
        }
      }
    } catch (error) {
      console.error('Error saving candidate status:', error);
      toast.error("An error occurred while saving the hiring request.");
      setStatusUpdating(false);
    }
  };

  // Recruiter / HR roles can move an Approved candidate: Approved -> Joining -> Joined.
  const canMarkJoin = ["recruiter", "hr specialist", "hr director", "hr manager", "admin"]
    .includes((getToken()?.role || "").toLowerCase());

  // Approved -> Joining (with a join date).
  const handleConfirmJoining = async () => {
    if (!joiningDateValue) {
      toast.error("Please select the joining date.");
      return;
    }
    if (statusUpdating) return;
    try {
      setStatusUpdating(true);
      const userName = getUserFromToken();
      candidateDetails.candidate.updated_by = userName;
      candidateDetails.candidate.oldstatus = candidateDetails.candidate.status;
      candidateDetails.candidate.status = "Joining";
      candidateDetails.candidate.joiningDate = joiningDateValue;
      candidateDetails.candidate.SendapprovalNotes = `Joining on ${joiningDateValue}`;

      const saved = await UpdateCandidateStaus(candidateDetails.candidate);
      if (saved?.statusCode === false) {
        toast.error("Failed to update the candidate.");
      } else {
        toast.success("Candidate marked as Joining.");
        setCandidate(prev => ({ ...prev, status: "Joining", joiningDate: joiningDateValue }));
        setShowJoiningModal(false);
      }
    } catch (err) {
      console.error("Error marking joining:", err);
      toast.error("An error occurred.");
    } finally {
      setStatusUpdating(false);
    }
  };

  // Joining -> Joined; also sets the linked hiring request to "Joined".
  const handleMarkJoined = async () => {
    if (statusUpdating) return;
    setShowJoinedConfirm(false);
    try {
      setStatusUpdating(true);
      const userName = getUserFromToken();
      candidateDetails.candidate.updated_by = userName;
      candidateDetails.candidate.oldstatus = candidateDetails.candidate.status;
      candidateDetails.candidate.status = "Joined";
      candidateDetails.candidate.SendapprovalNotes = "Candidate joined";

      const saved = await UpdateCandidateStaus(candidateDetails.candidate);
      if (saved?.statusCode === false) {
        toast.error("Failed to update the candidate.");
        return;
      }
      // Sync the hiring request to Joined at the same time.
      const req = candidateDetails.hiringRequests;
      if (req?.request_id) {
        try {
          await SaveHiringRequestStatus({
            Request_id: req.request_id,
            old_status: req.status,
            new_status: "Joined",
            logDescription: "Candidate joined",
            change_by: userName,
          });
          if (candidateDetails.hiringRequests) candidateDetails.hiringRequests.status = "Joined";
        } catch (e) {
          console.error("Failed to sync request status to Joined", e);
        }
      }
      toast.success("Candidate joined and request updated.");
      setCandidate(prev => ({ ...prev, status: "Joined" }));
    } catch (err) {
      console.error("Error marking joined:", err);
      toast.error("An error occurred.");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleStatusSubmit = async () => {
    try {
      if (!finalStatus || finalStatus.trim() === '') {
        toast.error("Please select a valid final status before submitting.");
        return;
      }

      let candidateStatusRequest = null;
      setIsSubmitting(true);
      const userName = getUserFromToken();
      candidateDetails.candidate.updated_by = userName;
      candidateDetails.candidate.oldstatus = candidateDetails.candidate.status;
      candidateDetails.candidate.status = finalStatus;

      const savedRequest = await UpdateCandidateStaus(candidateDetails.candidate);

      if (savedRequest.statusCode === false) {
        toast.error("Failed to update the candidate.");
      } else {
        toast.success(`Candidate marked as ${finalStatus}`);

        const hiringManageremail = hiringManagers.find(manager => Number(manager.user_id) === Number(candidateDetails.hiringRequests.hiring_manager))?.email;

        const formattedHiringRequest = {
          requestId: candidateDetails.hiringRequests.request_id,
          position: candidateDetails.hiringRequests.position,
          requestType: candidateDetails.hiringRequests.request_type,
        };
        const formattedCandidates = [candidateDetails.candidate].map(c => ({
          full_name: c.full_name,
          email: c.email,
          phone_number: c.phone_number || "",
          last_job_title: c.last_job_title || "Not available"
        }));
        candidateStatusRequest = {
          recipientName: "Bussiness Owner",
          recipientEmail: hiringManageremail,
          isApprovalRequest: finalStatus === "Send For Initial Approval",
          isRecruiter: false,
          status: finalStatus,
          notes: comments,
          hiringRequest: formattedHiringRequest,
          candidates: formattedCandidates,
        };

        setCandidate(prev => ({
          ...prev,
          candidate: {
            ...prev.candidate,
            status: finalStatus,
          },
        }));

        setFinalStatus('');
        setShowHiringModal(false);
        setIsSubmitting(false);
        await SendCandidateStatusEmailAsync(candidateStatusRequest);
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error("An error occurred.");
    }
  };

  const handleEditCandidate = async () => {
    const validationErrors = {};

    if (!formData.full_name || formData.full_name.trim() === "") {
      validationErrors.full_name = "Full name is required.";
    }

    if (!formData.email || formData.email.trim() === "") {
      validationErrors.email = "Email is required.";
    }

    if (!formData.phone_number || formData.phone_number.trim() === "") {
      validationErrors.phone_number = "Phone number is required.";
    } else if (!/^\d{7,15}$/.test(formData.phone_number.trim())) {
      validationErrors.phone_number = "Enter a valid phone number (7-15 digits).";
    }

    if (!formData.date_of_birth || formData.date_of_birth.trim() === "") {
      validationErrors.date_of_birth = "Date of birth is required.";
    } else if (isNaN(Date.parse(formData.date_of_birth))) {
      validationErrors.date_of_birth = "Enter a valid date of birth.";
    }

    if (!formData.experience_years || isNaN(formData.experience_years)) {
      validationErrors.experience_years = "Valid years of experience is required.";
    }

    if (!formData.last_salary || isNaN(formData.last_salary)) {
      validationErrors.last_salary = "Valid current salary is required.";
    }
    if (!formData.expected_salary || isNaN(formData.expected_salary)) {
      validationErrors.expected_salary = "Valid expected salary is required.";
    }

    if (!formData.last_job_title || formData.last_job_title.trim() === "") {
      validationErrors.last_job_title = "Current position is required.";
    }
    if (!formData.company_name || formData.company_name.trim() === "") {
      validationErrors.company_name = "Current employer is required.";
    }

    if (!formData.highest_degree || formData.highest_degree.trim() === "") {
      validationErrors.highest_degree = "Highest degree is required.";
    }

    if (!formData.descriptions || formData.descriptions.trim() === "") {
      validationErrors.descriptions = "Recruiter notes are required.";
    }

    if (Object.keys(validationErrors).length > 0) {
      Object.values(validationErrors).forEach((msg) =>
        toast.error(msg)
      );
      return;
    }

    try {
      setStatusUpdating(true);
      const cleanedCandidate = cleanNumericFields(formData);
      const response = await UpdateCandidate(cleanedCandidate);

      if (response && !response.status) {
        toast.success("Candidate updated successfully!");
        setCandidate((prev) => ({
          ...prev,
          candidate: {
            ...prev.candidate,
            ...cleanedCandidate,
          },
        }));

        handleEditClose();
        getCandidateDetails();
      } else {
        toast.error(`Update failed with status: ${response.status}`);
      }
    } catch (error) {
      toast.error("An error occurred while updating the candidate.");
      console.error(error);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleSendForApprvalSubmit = async () => {
    let candidateStatusRequest = null;
debugger
    try {
      const hiringManageremail = hiringManagers.find(manager => Number(manager.user_id) === Number(candidateDetails.hiringRequests.hiring_manager))?.email;
      setIsSubmitting(true);
      const userName = getUserFromToken();
      candidateDetails.candidate.updated_by = userName;
      candidateDetails.candidate.oldstatus = candidateDetails.candidate.status;
      candidateDetails.candidate.status = "Send For Approval";
      candidateDetails.candidate.SendapprovalNotes = comments;
      candidateDetails.candidate.hiringManageremail = hiringManageremail;

      const savedRequest = await SendForApproval(candidateDetails.candidate);

      if (savedRequest.statusCode === false) {
        toast.error("Failed to update the Candidates.");
      } else {
        toast.success(`Candidate Send For Approval`, { position: 'top-right' });

        setCandidate(prev => ({
          ...prev,
          candidate: {
            ...prev.candidate,
          },
        }));
debugger
        setshowSendForApprovalModal(false);
        setIsSubmitting(false);
debugger
        // Send email to approval level role (returned from API), CC business owner
        const approvalRole = savedRequest.approvalLevelName;
        if (approvalRole) {
          await SendApprovalWorkflowEmailAsync({
            recipientRole: approvalRole,
            companyCode: getCompanyCodeFromToken() || '',
            hiringManagerUserId: Number(candidateDetails.hiringRequests?.hiring_manager) || null,
            requestId: candidateDetails.hiringRequests?.request_id || 0,
            referenceNumber: candidateDetails.candidate?.ReferenceNumber || '',
            action: "Candidate Sent For Approval",
            actorName: userName,
            actorRole: getToken()?.role || '',
            nextStep: `Pending ${approvalRole} Approval`,
            notes: `${candidateDetails.candidate.full_name} - ${comments || ''}`.trim(),
            department: candidateDetails.candidate?.department_name || '',
            position: candidateDetails.candidate?.role || candidateDetails.hiringRequests?.position || '',
            requestType: candidateDetails.hiringRequests?.request_type || '',
          });
        }
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error("An error occurred.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDocument(null);
  };

  const handleStatusChangeInitial = () => {
    try {
      setStatuIsModalOpenInitial(true);
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const handleStatusChange = () => {
    try {
      setStatuIsModalOpen(true);
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const handleStatusChangeShortlist = () => {
    try {
      setStatuIsModalOpenShortList(true);
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const handleHiringPackageModalOpen = () => {
    const existing = candidateDetails?.salaryDetails;

    if (existing && existing.basicSalary) {
      setIsEditMode(true);
      setSalaryDetails({
        basicSalary: existing.basicSalary || '',
        accommodation: existing.accommodation || '',
        transportation: existing.transportation || '',
        additionalComments: existing.additionalComments || '',
      });
    } else {
      setIsEditMode(false);
      setSalaryDetails({
        basicSalary: '',
        accommodation: '',
        transportation: '',
        additionalComments: '',
      });
    }

    setHiringPackageModal(true);
  };

  const handleApprvalSubmit = async () => {
    if (!FinalApprovalStatus) {
      toast.error("Please select an approval status (Approve/Reject).");
      return;
    }

    if (FinalApprovalStatus === "Approved" && !comments) {
      toast.error("Please provide comments for the Approve.");
      return;
    }
    if (FinalApprovalStatus === "Rejected" && !comments) {
      toast.error("Please provide comments for the rejection.");
      return;
    }
    if (FinalApprovalStatus === "Send Back" && !comments) {
      toast.error("Please provide comments for the Send Back.");
      return;
    }

    try {
      setStatusLoading(true);

      const candidateId = candidateDetails?.candidate?.candidate_id;

      if (!candidateId) {
        toast.error("Candidate details are missing.");
        return;
      }

      const userName = getUserFromToken();
      candidateDetails.candidate.updated_by = userName;
      candidateDetails.candidate.oldstatus = candidateDetails.candidate.status;
      candidateDetails.candidate.substatus = `${FinalApprovalStatus} Level ${candidateDetails?.candidate?.approval_level_order}`;

      candidateDetails.candidate.status = FinalApprovalStatus;
      candidateDetails.candidate.SendapprovalNotes = comments;
      candidateDetails.candidateApprovals.approval_level = candidateDetails.candidate.approval_level_name;
      candidateDetails.candidateApprovals.approver = userName;
      candidateDetails.candidateApprovals.approval_status = FinalApprovalStatus;
      candidateDetails.candidateApprovals.comments = comments;
      candidateDetails.candidateApprovals.approval_date = new Date();

      const savedRequest = await InsertApprovalLevel(candidateDetails.candidate);

      if (savedRequest.statusCode === false) {
        toast.error("Failed to update the candidate approval.");
      } else {
        toast.success("Candidate Approval submitted successfully!");

        setCandidate(prev => ({
          ...prev,
          candidate: {
            ...prev.candidate,
            status: candidateDetails.candidate.status,
            updated_by: userName,
            oldstatus: prev.candidate.status,
            SendapprovalNotes: comments,
          },
          candidateApprovals: [
            {
              approval_level: candidateDetails.candidate.approval_level_name,
              approver: userName,
              approval_status: FinalApprovalStatus,
              comments: comments,
              approval_date: new Date()
            },
            ...prev.candidateApprovals
          ],
        }));

        setStatusUpdating(false);
        setshowApprovalModal(false);
        setStatusLoading(false);
debugger
        // Send email to next approver or recruiter based on status
        try {
          if (FinalApprovalStatus === "Approved") {
            // If there's a next approval level, email goes to that role
            const nextLevel = savedRequest.approvalLevelName;
            if (nextLevel) {
              await SendApprovalWorkflowEmailAsync({
                recipientRole: nextLevel,
                companyCode: getCompanyCodeFromToken() || '',
                hiringManagerUserId: Number(candidateDetails.hiringRequests?.hiring_manager) || null,
                requestId: candidateDetails.hiringRequests?.request_id || 0,
                referenceNumber: candidateDetails.candidate?.ReferenceNumber || '',
                action: `Candidate Approved by ${candidateDetails.candidate.approval_level_name}`,
                actorName: userName,
                actorRole: getToken()?.role || '',
                nextStep: `Pending ${nextLevel} Approval`,
                notes: `${candidateDetails.candidate.full_name} - ${comments || ''}`.trim(),
                department: candidateDetails.candidate?.department_name || '',
                position: candidateDetails.candidate?.role || candidateDetails.hiringRequests?.position || '',
                requestType: candidateDetails.hiringRequests?.request_type || '',
              });
            } else {
              // Final approval — email recruiter
              const recruiterEmail = hiringManagers.find(m => Number(m.user_id) === Number(candidateDetails.hiringRequests.recruiter_id))?.email;
              if (recruiterEmail) {
                await SendApprovalWorkflowEmailAsync({
                  toEmails: recruiterEmail,
                  companyCode: getCompanyCodeFromToken() || '',
                  requestId: candidateDetails.hiringRequests?.request_id || 0,
                  referenceNumber: candidateDetails.candidate?.ReferenceNumber || '',
                  action: "Candidate Fully Approved",
                  actorName: userName,
                  actorRole: getToken()?.role || '',
                  nextStep: "Proceed with Hiring",
                  notes: `${candidateDetails.candidate.full_name} has been fully approved.`,
                  department: candidateDetails.candidate?.department_name || '',
                  position: candidateDetails.candidate?.role || candidateDetails.hiringRequests?.position || '',
                  requestType: candidateDetails.hiringRequests?.request_type || '',
                });
              }
            }
          } else {
            // Rejected or Send Back — email recruiter
            const recruiterEmail = hiringManagers.find(m => Number(m.user_id) === Number(candidateDetails.hiringRequests.recruiter_id))?.email;
            if (recruiterEmail) {
              await SendApprovalWorkflowEmailAsync({
                toEmails: recruiterEmail,
                companyCode: getCompanyCodeFromToken() || '',
                requestId: candidateDetails.hiringRequests?.request_id || 0,
                referenceNumber: candidateDetails.candidate?.ReferenceNumber || '',
                action: `Candidate ${FinalApprovalStatus}`,
                actorName: userName,
                actorRole: getToken()?.role || '',
                nextStep: FinalApprovalStatus === "Send Back" ? "Review and Resubmit" : "Candidate Rejected",
                notes: `${candidateDetails.candidate.full_name} - ${comments || ''}`.trim(),
                department: candidateDetails.candidate?.department_name || '',
                position: candidateDetails.candidate?.role || candidateDetails.hiringRequests?.position || '',
                requestType: candidateDetails.hiringRequests?.request_type || '',
              });
            }
          }
        } catch (emailErr) {
          console.error("Failed to send approval email:", emailErr);
        }

        await getCandidateDetails();
      }
    } catch (error) {
      toast.error("There was an error submitting your decision. Please try again.");
      console.error("Error submitting approval:", error);
    }
  };

  const handleUploadDocument = async (e) => {
    if (statusUpdating) return;
    e.preventDefault();
    setStatusUpdating(true);
    try {
      const savedRequest = await UploadDocuments(formData.Documents);

      if (savedRequest.statusCode === false) {
        toast.error("Failed to update the Candidates.");
      } else {
        toast.success(`Uploaded the Documents ${formData.Documents.process_type}`, { position: 'top-right' });

        setCandidate(prev => {
          const existingDocuments = Array.isArray(prev.documents) ? prev.documents : [];
          const updatedDocuments = [...existingDocuments, formData.Documents];

          return {
            ...prev,
            documents: updatedDocuments
          };
        });

        setStatusUpdating(false);
        setDocumentIsModalOpen(false);
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error("An error occurred.");
    }
  };

  const handleHiringPackageModalSubmit = async () => {
    if (statusUpdating) return;

    setStatusUpdating(true);

    const basic = Number(salaryDetails.basicSalary);
    const accom = Number(salaryDetails.accommodation);
    const transpo = Number(salaryDetails.transportation);

    if (!basic || basic <= 0) {
      toast.error("Basic Salary is required and must be greater than 0.");
      setStatusUpdating(false);
      return;
    }

    if (!accom || accom <= 0) {
      toast.error("Accommodation Allowance is required and must be greater than 0.");
      setStatusUpdating(false);
      return;
    }

    if (!transpo || transpo <= 0) {
      toast.error("Transportation Allowance is required and must be greater than 0.");
      setStatusUpdating(false);
      return;
    }

    try {
      setIsSubmitting(true);
      const userName = getUserFromToken();

      const hiringPackageData = {
        candidate_id: candidateDetails.candidate?.candidate_id,
        basicSalary: basic,
        accommodation: accom,
        transportation: transpo,
        additionalComments: salaryDetails.additionalComments,
        updated_by: userName,
        updated_at: new Date().toISOString(),
      };

      const response = isEditMode
        ? await UpdateHiringPackage(hiringPackageData)
        : await InsertHiringPackage(hiringPackageData);

      if (response.statusCode === false) {
        toast.error(
          isEditMode
            ? "Failed to update the Hiring Package."
            : "Failed to insert the Hiring Package."
        );
      } else {
        toast.success(
          isEditMode
            ? "Hiring Package updated successfully!"
            : "Hiring Package added successfully!"
        );

        setCandidate(prev => ({
          ...prev,
          salaryDetails: {
            ...prev.salaryDetails,
            ...hiringPackageData,
          },
        }));

        candidateDetails.candidate.status = 'Under Hiring Process';
        setHiringPackageModal(false);
        setIsEditMode(false);
        setIsSubmitting(false);
        setSalaryDetails({
          basicSalary: '',
          accommodation: '',
          transportation: '',
          additionalComments: '',
        });
      }
    } catch (error) {
      console.error("Hiring Package Error:", error);
      toast.error("An error occurred while saving the Hiring Package.");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleOfferLetterModalOpen = async () => {
    try {
      setStatusUpdating(true);
      const response = await fetch('/OfferLetter.html');
      let html = await response.text();

      const basicSalary = candidateDetails.salaryDetails?.basicSalary || 0;
      const accommodation = candidateDetails.salaryDetails?.accommodation || 0;
      const transportation = candidateDetails.salaryDetails?.transportation || 0;
      const totalPackage = Number(basicSalary) + Number(accommodation) + Number(transportation);

      const today = new Date();
      const joinDate = new Date(today);
      joinDate.setDate(today.getDate() + 30);

      const formattedJoinDate = joinDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      html = html
        .replace(/{{candidate_name}}/g, candidateDetails.candidate.full_name)
        .replace(/{{position}}/g, candidateDetails.hiringRequests?.position || candidateDetails.candidate.last_job_title)
        .replace(/{{department}}/g, candidateDetails.candidate.department_name || '')
        .replace(/{{basic_salary}}/g, Number(basicSalary).toLocaleString())
        .replace(/{{accommodation}}/g, Number(accommodation).toLocaleString())
        .replace(/{{transportation}}/g, Number(transportation).toLocaleString())
        .replace(/{{total_package}}/g, totalPackage.toLocaleString())
        .replace(/{{join_date}}/g, formattedJoinDate)
        .replace(/{{offer_date}}/g, today.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }))
        .replace(/{{hr_name}}/g, getUserFromToken() || 'HR Department')
        .replace(/{{company_name}}/g, 'Your Company Name')
        .replace(/{{reference_number}}/g, candidateDetails.candidate.referenceNumber || '');

      setOfferLetterHtml(html);
      setShowOfferLetterModalOpen(true);
    } catch (error) {
      console.error('Failed to load OfferLetter.html:', error);
      toast.error('Failed to generate offer letter. Please try again.');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleOfferLetterModalClose = () => {
    setShowOfferLetterModalOpen(false);
  };

  const handleDownloadPdf = () => {
    const element = document.getElementById('offer-letter-html');
    if (!element) return;

    const opt = {
      margin: 0.5,
      filename: 'OfferLetter.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save();
  };

  const getStatusButtonText = (status) => {
    switch (status) {
      case 'Approved': return 'Approve Candidate';
      case 'Rejected': return 'Reject Candidate';
      case 'Send Back': return 'Send Back';
      default: return 'Save';
    }
  };

  const getStatusBorderColor = (status) => {
    switch (status) {
      case 'Shortlisted For Interview': return 'rgb(59, 130, 246)';
      case 'Ready For Evaluation': return 'rgb(20, 184, 166)';
      case 'Rejected': return 'rgb(239, 68, 68)';
      case 'On Hold': return 'rgb(245, 158, 11)';
      default: return 'rgba(18, 76, 114, 0.3)';
    }
  };

  const getStatusBackgroundColor = (status) => {
    switch (status) {
      case 'Shortlisted For Interview': return 'rgba(59, 130, 246, 0.1)';
      case 'Ready For Evaluation': return 'rgba(20, 184, 166, 0.1)';
      case 'Rejected': return 'rgba(239, 68, 68, 0.1)';
      case 'On Hold': return 'rgba(245, 158, 11, 0.1)';
      default: return 'rgba(18, 76, 114, 0.05)';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'Shortlisted For Interview': return 'rgb(30, 64, 175)';
      case 'Ready For Evaluation': return 'rgb(13, 148, 136)';
      case 'Rejected': return 'rgb(185, 28, 28)';
      case 'On Hold': return 'rgb(180, 83, 9)';
      default: return 'rgb(18, 76, 114)';
    }
  };

  const getStatusButtonColor = (status) => {
    switch (status) {
      case 'Shortlisted For Interview': return 'rgb(59, 130, 246)';
      case 'Ready For Evaluation': return 'rgb(20, 184, 166)';
      case 'Rejected': return 'rgb(239, 68, 68)';
      case 'On Hold': return 'rgb(245, 158, 11)';
      default: return 'rgb(18, 76, 114)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Shortlisted For Interview':
        return (
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'Ready For Evaluation':
        return (
          <svg className="w-5 h-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        );
      case 'Rejected':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'On Hold':
        return (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getHiringManagerStatusBorderColor = (status) => {
    switch (status) {
      case 'Approved By Hiring Manager': return 'rgb(34, 197, 94)';
      case 'Rejected By Hiring Manager': return 'rgb(239, 68, 68)';
      default: return 'rgba(18, 76, 114, 0.3)';
    }
  };

  const getHiringManagerStatusBackgroundColor = (status) => {
    switch (status) {
      case 'Approved By Hiring Manager': return 'rgba(34, 197, 94, 0.1)';
      case 'Rejected By Hiring Manager': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(18, 76, 114, 0.05)';
    }
  };

  const getHiringManagerStatusTextColor = (status) => {
    switch (status) {
      case 'Approved By Hiring Manager': return 'rgb(22, 163, 74)';
      case 'Rejected By Hiring Manager': return 'rgb(185, 28, 28)';
      default: return 'rgb(18, 76, 114)';
    }
  };

  const getHiringManagerStatusButtonColor = (status) => {
    switch (status) {
      case 'Approved By Hiring Manager': return 'rgb(34, 197, 94)';
      case 'Rejected By Hiring Manager': return 'rgb(239, 68, 68)';
      default: return 'rgb(18, 76, 114)';
    }
  };

  const getHiringManagerStatusIcon = (status) => {
    switch (status) {
      case 'Approved By Hiring Manager':
        return (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'Rejected By Hiring Manager':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getHiringManagerStatusDisplayName = (status) => {
    switch (status) {
      case 'Approved By Hiring Manager': return 'Approve Candidate';
      case 'Rejected By Hiring Manager': return 'Reject Candidate';
      default: return status;
    }
  };

  const getInitialStatusBorderColor = (status) => {
    switch (status) {
      case 'Send For Initial Approval': return 'rgb(59, 130, 246)';
      case 'Rejected By Recruiter': return 'rgb(239, 68, 68)';
      default: return 'rgba(18, 76, 114, 0.3)';
    }
  };

  const getInitialStatusBackgroundColor = (status) => {
    switch (status) {
      case 'Send For Initial Approval': return 'rgba(59, 130, 246, 0.1)';
      case 'Rejected By Recruiter': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(18, 76, 114, 0.05)';
    }
  };

  const getInitialStatusTextColor = (status) => {
    switch (status) {
      case 'Send For Initial Approval': return 'rgb(30, 64, 175)';
      case 'Rejected By Recruiter': return 'rgb(185, 28, 28)';
      default: return 'rgb(18, 76, 114)';
    }
  };

  const getInitialStatusButtonColor = (status) => {
    switch (status) {
      case 'Send For Initial Approval': return 'rgb(59, 130, 246)';
      case 'Rejected By Recruiter': return 'rgb(239, 68, 68)';
      default: return 'rgb(18, 76, 114)';
    }
  };

  const getInitialStatusIcon = (status) => {
    switch (status) {
      case 'Send For Initial Approval':
        return (
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
          </svg>
        );
      case 'Rejected By Recruiter':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getInitialStatusDisplayName = (status) => {
    switch (status) {
      case 'Send For Initial Approval': return 'Send for Initial Approval';
      case 'Rejected By Recruiter': return 'Reject Candidate';
      default: return status;
    }
  };

  const spinnerStyles = {
    spinner: {
      border: '4px solid rgba(18, 76, 114, 0.1)',
      borderLeftColor: 'rgb(18, 76, 114)',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      animation: 'spin 1s linear infinite'
    }
  };

  const spinnerKeyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes progress {
      0% { width: 0%; margin-left: 0%; }
      50% { width: 100%; margin-left: 0%; }
      100% { width: 0%; margin-left: 100%; }
    }
  `;

  if (loading) {
    return (
      <>
        <style>{spinnerKeyframes}</style>
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-8">
          <div className="relative">
            <div style={spinnerStyles.spinner}></div>
            <div className="absolute inset-0 rounded-full animate-pulse" 
                 style={{ 
                   backgroundColor: 'rgba(18, 76, 114, 0.1)',
                   transform: 'scale(1.5)',
                   zIndex: -1
                 }}>
            </div>
          </div>
          <div className="mt-6 text-center">
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'rgb(18, 76, 114)' }}>
              Loading Candidates..
            </h3>
            <p className="text-sm" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
              Please wait while we fetch the latest data...
            </p>
            <div className="mt-4 w-48 h-1 bg-gray-200 rounded-full overflow-hidden mx-auto">
              <div className="h-full rounded-full" 
                   style={{ 
                     backgroundColor: 'rgb(18, 76, 114)',
                     animation: 'progress 1.5s ease-in-out infinite'
                   }}>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  if (!candidateDetails?.candidate) {
    return (
      <div className="text-center mt-10">
        <h2 className="text-xl font-semibold">No candidate found for the provided ID.</h2>
        <button
          onClick={() => navigate('/')}
          className="mt-4 text-white bg-blue-500 px-4 py-2 rounded-md"
        >
          Go Back to Home
        </button>
      </div>
    );
  }

  // Co-op positions (grade C, position stored as "C-…") use a simplified candidate flow:
  // the recruiter/HR Specialist adds the candidate and sends it straight to HR Director
  // approval — no interview scheduling and no hiring package.
  const isCoop = (candidateDetails?.hiringRequests?.position || '').toUpperCase().startsWith('C-');

  return (
    <div className="min-h-screen bg-white mx-4 sm:h-[72vh] sm:pt-4 sm:px-4 sm:rounded-t-[32px] md:h-[74.5vh] md:pt-8 md:px-[calc(5%-1rem)] md:rounded-t-[40px] lg:h-[74.5vh] lg:px-[calc(6%-1rem)] xl:h-[100%] xl:pt-24 xl:px-[calc(2.5%-1rem)] xl:rounded-t-[48px] 2xl:h-[74.5vh] 2xl:px-[calc(8%-1rem)] 3xl:h-[74.5vh] 3xl:px-[calc(8%-1rem)] 4xl:h-[74.5vh] 4xl:px-[calc(10%-1rem)]">

      <div className='relative xl:-top-[165px]'>
        <div className="flex flex-col sm:flex-row items-center gap-4 p-6 sm:gap-6 bg-white rounded-xl shadow-lg border border-gray-100">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden border-4" style={{ borderColor: PRIMARY_LIGHT }}>
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(candidateDetails.candidate.full_name)}&background=${PRIMARY_COLOR.replace('rgb(', '').replace(')', '')}&color=fff`}
                alt={candidateDetails.candidate.full_name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-2 -right-2 bg-white rounded-full p-2 shadow-lg" style={{ border: `1px solid ${PRIMARY_LIGHT}` }}>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: candidateDetails.candidate.status === 'Approved' ? SUCCESS_COLOR : INFO_COLOR }}></div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold" style={{ color: PRIMARY_COLOR }}>
              {candidateDetails.candidate.full_name}
            </h1>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" style={{ color: PRIMARY_COLOR }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {candidateDetails.hiringRequests.request_type}
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" style={{ color: PRIMARY_COLOR }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {candidateDetails.hiringRequests.request_type === "Exceptional" || candidateDetails.hiringRequests.request_type === "Outsourced"
                  ? candidateDetails.hiringRequests.role_text
                  : candidateDetails.candidate.role}
              </span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" style={{ color: PRIMARY_COLOR }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                {candidateDetails.candidate.department_name}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2 flex-shrink-0">
            {(getToken()?.role === 'CEO' || getToken()?.role === 'Chief Finance') && (
            <button
              onClick={async () => {
                setShowMoreInfoPopup(true);
                setMoreInfoTab('personal');
                if (candidateDetails.hiringRequests?.request_id) {
                  try {
                    const history = await HiringRequestStatusHistory(candidateDetails.hiringRequests.request_id);
                    setHrAuditTrail(history || []);
                  } catch (e) { console.error(e); }
                }
              }}
              className="px-5 py-2.5 text-sm rounded-lg transition-all flex items-center gap-1 border"
              style={{ color: PRIMARY_COLOR, borderColor: 'rgba(18,76,114,0.2)', backgroundColor: 'rgba(18,76,114,0.05)' }}
            >
              ℹ️ More Info
            </button>
            )}
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-100 text-gray-700 px-5 py-2.5 text-sm rounded-lg hover:bg-gray-200 transition-all flex items-center gap-1 border border-gray-200"
            >
              ← Back
            </button>

            {permissions.canEditCandidate && permissions.isStatusEditable && (
              <button
                onClick={handleEdit}
                className="bg-blue-600 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-blue-700 transition-all"
              >
                Edit Candidate
              </button>
            )}

            {permissions.canSendForInitialApproval && !isCoop && candidateDetails.candidate.status === 'Applied' && (
              <button
                onClick={handleStatusChangeInitial}
                disabled={statusUpdating}
                className="bg-blue-500 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-blue-600 transition-all"
              >
                Send For Initial Approval
              </button>
            )}

            {permissions.canRejectApplied && candidateDetails.candidate.status === 'Applied' && (
              <button
                onClick={() => {
                  setSelectedStatus('Rejected By Recruiter');
                  setComments('');
                  setStatuIsModalOpenInitial(true);
                }}
                disabled={statusUpdating}
                className="bg-red-500 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-red-600 transition-all"
              >
                Reject Candidate
              </button>
            )}

            {permissions.canInitialApproval && candidateDetails.candidate.status === 'Send For Initial Approval' && (
              <button
                onClick={handleStatusChange}
                disabled={statusUpdating}
                className="bg-green-500 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-green-600 transition-all"
              >
                + Initial Approval
              </button>
            )}

            {permissions.canShortlistForInterview &&
              (candidateDetails.candidate.status === 'Approved By Hiring Manager' ||
                candidateDetails.candidate.status === 'On Hold') && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedStatus('Shortlisted For Interview');
                      setComments('');
                      setStatuIsModalOpenShortList(true);
                    }}
                    disabled={statusUpdating}
                    className="bg-purple-500 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-purple-600 transition-all"
                  >
                    + Shortlist & Schedule Interview
                  </button>
                 
                </div>
              )}

       {permissions.canScheduleInterview && 
  (candidateDetails.candidate.status === 'Shortlisted For Interview' ||
   (candidateDetails.candidate.status === 'Interview Scheduled' && 
    (!candidateDetails.interviews?.some(i => i.status === 'Interview Scheduled') ||
     candidateDetails.interviews?.every(i => i.status === 'Interview Cancelled')))) && (
  <button
    onClick={() => {
      setCancelInterview(null);
      setEditInterview(null);
      setInterviewDate(null);
      setInterviewTimeFrom('');
      setInterviewTimeTo('');
      setInterviewType('');
      setInterviewBoard([]);
      setInterviewNotes('');
      handleInterviewModalOpen();
    }}
    disabled={statusUpdating}
    className="bg-amber-500 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-amber-600 transition-all"
  >
    + Schedule Interview
  </button>
)}

      {permissions.canAddEvaluation &&
  (candidateDetails.candidate.status === 'Interview Scheduled' ||
    candidateDetails.candidate.status === 'Ready For Evaluation' ||
    candidateDetails.candidate.status === 'Under Evaluation' ||
    candidateDetails.candidate.status === 'Send Back') &&
  (candidateDetails.candidate.status === 'Ready For Evaluation' ||
    candidateDetails.candidate.status === 'Under Evaluation' ||
    candidateDetails?.interviews?.some(i => i.status === 'Interview Scheduled')) && (
    <button
      onClick={handleEvaluationModalOpen}
      disabled={statusUpdating}
      className="bg-indigo-500 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-indigo-600 transition-all flex items-center gap-2"
    >
      + Evaluation
    </button>
  )}

            {permissions.canShortlistAfterEvaluation &&
              (candidateDetails.candidate.status === 'Under Evaluation' ||
                candidateDetails.candidate.status === 'Send Back') && (
                <button
                  onClick={handleHiringModalOpen}
                  disabled={statusUpdating}
                  className="bg-cyan-500 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-cyan-600 transition-all flex items-center gap-2"
                >
                  + Shortlist
                </button>
              )}

            {permissions.canStartHiringProcess && candidateDetails.candidate.status === 'ShortListed' && (
              <button
                onClick={handleHiringPackageModalOpen}
                disabled={statusUpdating}
                className="bg-emerald-500 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-emerald-600 transition-all"
              >
                + Hiring Process
              </button>
            )}

            {permissions.canSendForApproval &&
              (candidateDetails.candidate.status === 'Under Hiring Process' ||
                candidateDetails.candidate.status === 'Send Back' ||
                (isCoop && candidateDetails.candidate.status === 'Applied')) && (
                <button
                  onClick={handleSendforApprovalModalOpen}
                  disabled={statusUpdating}
                  className="bg-blue-500 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-blue-600 transition-all"
                >
                  + Send For Approval
                </button>
              )}

            {permissions.canApprove && permissions.isCurrentApprover &&
              candidateDetails?.candidate?.status === 'Send For Approval' && (
                <button
                  onClick={handleApprovalModalOpen}
                  disabled={statusUpdating}
                  className={`px-6 py-2 rounded-lg shadow-sm ${statusUpdating
                    ? 'bg-emerald-400 cursor-not-allowed text-white'
                    : 'bg-emerald-500 text-white hover:bg-emerald-600'
                    }`}
                >
                  + Approve
                </button>
              )}

            {canMarkJoin && candidateDetails?.candidate?.status === 'Approved' && (
              <button
                onClick={() => { setJoiningDateValue(""); setShowJoiningModal(true); }}
                disabled={statusUpdating}
                className="bg-teal-500 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-teal-600 transition-all disabled:opacity-60"
              >
                Mark as Joining
              </button>
            )}

            {canMarkJoin && candidateDetails?.candidate?.status === 'Joining' && (
              <button
                onClick={() => setShowJoinedConfirm(true)}
                disabled={statusUpdating}
                className="bg-primary-800 text-white px-5 py-2.5 text-sm rounded-lg hover:bg-primary-800/80 transition-all disabled:opacity-60"
              >
                Mark as Joined
              </button>
            )}

            {permissions.canUploadDocuments && permissions.isStatusEditable && (
              <button
                onClick={() => setDocumentIsModalOpen(true)}
                className="px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-all"
                style={{
                  backgroundColor: PRIMARY_COLOR,
                  color: 'white',
                  boxShadow: `0 10px 15px -3px ${SHADOW_COLOR}`
                }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Upload Document
              </button>
            )}

            {false && permissions.canGenerateOfferLetter && candidateDetails?.candidate?.status === 'Approved' && (
              <button
                onClick={handleOfferLetterModalOpen}
                className="bg-white text-blue-600 px-6 py-2 rounded-lg shadow-sm border border-blue-200 text-base xl:text-lg hover:bg-blue-50 transition-all duration-200 flex items-center gap-2"
                style={{
                  borderColor: 'rgba(18, 76, 114, 0.2)',
                  color: PRIMARY_COLOR
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.05)';
                  e.currentTarget.style.borderColor = PRIMARY_COLOR;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = 'rgba(18, 76, 114, 0.2)';
                }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Generate Offer Letter
              </button>
            )}
          </div>
        </div>

        {/* Hiring Request Info Bar */}
        {candidateDetails.hiringRequests && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-4 px-4 py-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(18,76,114,0.03)', border: '1px solid rgba(18,76,114,0.08)' }}>
            <span style={{ color: 'rgba(18,76,114,0.5)' }}>Hiring Request</span>
            <span className="font-semibold" style={{ color: PRIMARY_COLOR }}>{candidateDetails.hiringRequests.referenceNumber || candidateDetails.hiringRequests.ReferenceNumber || '—'}</span>
            <span style={{ color: 'rgba(18,76,114,0.4)' }}>·</span>
            <span style={{ color: PRIMARY_COLOR }}>{candidateDetails.hiringRequests.position}</span>
            <span style={{ color: 'rgba(18,76,114,0.4)' }}>·</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(18,76,114,0.08)', color: PRIMARY_COLOR }}>{candidateDetails.hiringRequests.request_type}</span>
            {candidateDetails.hiringRequests.hiring_Type && (
              <><span style={{ color: 'rgba(18,76,114,0.4)' }}>·</span><span style={{ color: PRIMARY_COLOR }}>{candidateDetails.hiringRequests.hiring_Type}</span></>
            )}
          </div>
        )}

        <div className="mb-6 mt-4">
          <ul className="flex flex-wrap gap-2">
            {['details', 'Interview', 'Evaluation', 'Package', 'Approvals', 'documents']
              .filter((tab) => !(isCoop && (tab === 'Interview' || tab === 'Package')))
              .map((tab) => (
              <li
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`cursor-pointer py-3 px-6 rounded-lg font-medium transition-all ${activeTab === tab
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-800'
                  }`}
                style={{
                  backgroundColor: activeTab === tab ? 'rgb(18, 76, 114)' : 'white',
                  border: `1px solid ${activeTab === tab ? 'rgb(18, 76, 114)' : 'rgba(18, 76, 114, 0.1)'}`,
                  boxShadow: activeTab === tab ? '0 4px 6px -1px rgba(18, 76, 114, 0.1)' : 'none',
                }}
              >
                {tab === 'details'
                  ? 'Candidate Details'
                  : tab === 'Interview'
                    ? 'Interviews'
                    : tab === 'Evaluation'
                      ? 'Evaluation'
                      : tab === 'Package'
                        ? 'Hiring Package'
                        : tab === 'Approvals'
                          ? 'Approvals'
                          : 'Documents'}
              </li>
            ))}
          </ul>
        </div>

        {(candidateDetails.candidate.status === "Send For Approval" ||
          candidateDetails.candidate.status === "Approved" ||
          candidateDetails.candidate.status === "Hired") && (
            <CandidateApprovalFlow
              key={candidateDetails.candidate.candidate_id + candidateDetails.candidate.status}
              candidateId={candidateDetails.candidate.candidate_id}
            />
          )}
        
        <div className="bg-white rounded-2xl p-6 shadow-lg" style={{
          border: `1px solid ${PRIMARY_LIGHT}`,
          boxShadow: `0 5px 30px ${SHADOW_COLOR}`
        }}>
       {activeTab === 'details' && (
  <div className="space-y-4">
    {/* Status Banner */}
    <div className="flex items-center gap-3 p-4 rounded-xl" style={{
      backgroundColor: (() => {
        const s = candidateDetails?.candidate?.status;
        if (s === 'Approved' || s === 'Joined') return 'rgba(5,150,105,0.06)';
        if (s === 'Rejected' || s === 'Rejected By Recruiter') return 'rgba(239,68,68,0.06)';
        return 'rgba(18,76,114,0.04)';
      })(),
      border: '1px solid rgba(18,76,114,0.08)'
    }}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg" style={{
        backgroundColor: (() => {
          const s = candidateDetails?.candidate?.status;
          if (s === 'Approved' || s === 'Joined') return '#059669';
          if (s === 'Rejected' || s === 'Rejected By Recruiter') return '#ef4444';
          if (s === 'Under Evaluation' || s === 'Ready For Evaluation') return '#d97706';
          return PRIMARY_COLOR;
        })(),
        color: '#fff'
      }}>
        {(() => {
          const s = candidateDetails?.candidate?.status;
          if (s === 'Approved' || s === 'Joined') return '✓';
          if (s === 'Rejected' || s === 'Rejected By Recruiter') return '✗';
          return '⏳';
        })()}
      </div>
      <div>
        <p className="text-xs" style={{ color: 'rgba(18,76,114,0.5)' }}>Application Status</p>
        <p className="text-sm font-bold" style={{ color: PRIMARY_COLOR }}>
          {(() => {
            const status = candidateDetails?.candidate?.status;
            if (status === "Ready For Evaluation") return "Shortlisted - Ready for Evaluation";
            if (status === "Send For Approval") return candidateDetails?.candidate?.Substatus ?? 'Send For Approval';
            if (status === "Shortlisted For Interview") return "Shortlisted - Interview Required";
            return status || 'N/A';
          })()}
        </p>
      </div>
    </div>

    {/* Info Grid */}
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {[
        { icon: '👤', label: 'Full Name', value: candidateDetails.candidate.full_name },
        { icon: '🏢', label: 'Company', value: candidateDetails.candidate.company_name },
        { icon: '💼', label: 'Job Title', value: candidateDetails.candidate.last_job_title },
        { icon: '🎓', label: 'Education', value: candidateDetails.candidate.highest_degree },
        { icon: '📅', label: 'Experience', value: candidateDetails.candidate.experience_years ? `${candidateDetails.candidate.experience_years} years` : null },
        { icon: '💰', label: 'Last Salary', value: candidateDetails.candidate.last_salary ? `SAR ${Number(candidateDetails.candidate.last_salary).toLocaleString()}` : null },
        { icon: '💵', label: 'Expected Salary', value: candidateDetails.candidate.expected_salary ? `SAR ${Number(candidateDetails.candidate.expected_salary).toLocaleString()}` : null },
      ].filter(item => item.value).map((item, idx) => (
        <div key={idx} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.03)', border: '1px solid rgba(18,76,114,0.06)' }}>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm">{item.icon}</span>
            <span className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>{item.label}</span>
          </div>
          <p className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{item.value}</p>
        </div>
      ))}
    </div>

    {/* Notes */}
    {candidateDetails.candidate.descriptions && (
      <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.03)', border: '1px solid rgba(18,76,114,0.06)' }}>
        <p className="text-[11px] font-medium mb-1" style={{ color: 'rgba(18,76,114,0.5)' }}>Notes</p>
        <p className="text-sm" style={{ color: PRIMARY_COLOR }}>{candidateDetails.candidate.descriptions}</p>
      </div>
    )}
  </div>
)}

          {activeTab === 'documents' && (
            <div className="space-y-4">
              {/* Upload Button */}
              {permissions.canUploadDocuments && permissions.isStatusEditable && (
                <div className="flex justify-end">
                  <button onClick={() => setDocumentIsModalOpen(true)} className="px-4 py-2 text-sm rounded-lg font-medium flex items-center gap-2" style={{ backgroundColor: PRIMARY_COLOR, color: 'white' }}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                    Upload Document
                  </button>
                </div>
              )}

              {candidateDetails.documents && candidateDetails.documents.length > 0 ? (
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(18,76,114,0.08)' }}>
                  {candidateDetails.documents.map((doc, index) => {
                    const docType = doc.process_type === 'Other' ? doc.other_Process_type || 'Other' : doc.process_type || 'Document';
                    const fileSize = doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : '';
                    const isPdf = doc.mimeType?.includes('pdf');
                    const isImage = doc.mimeType?.startsWith('image/');
                    const fileIcon = isPdf ? '📄' : isImage ? '🖼️' : '📎';
                    const hasBlob = doc.storageType === 'Azure' && doc.blobUrl;
                    const downloadHref = hasBlob ? doc.blobUrl : `data:application/octet-stream;base64,${doc.fileBase64}`;

                    return (
                      <div key={index} className={`flex items-center gap-4 px-5 py-3 ${index > 0 ? 'border-t' : ''}`} style={{ borderColor: 'rgba(18,76,114,0.06)' }}>
                        {/* Icon */}
                        <span className="text-2xl flex-shrink-0">{fileIcon}</span>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: PRIMARY_COLOR }}>{docType}</p>
                          <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(18,76,114,0.5)' }}>
                            <span>{doc.fileName || 'file'}</span>
                            {fileSize && <><span>·</span><span>{fileSize}</span></>}
                            {doc.uploaded_At && <><span>·</span><span>{new Date(doc.uploaded_At).toLocaleDateString()}</span></>}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => handleOpenModal(doc)} className="px-3 py-1.5 text-xs rounded-lg font-medium" style={{ backgroundColor: 'rgba(18,76,114,0.08)', color: PRIMARY_COLOR }}>
                            Preview
                          </button>
                          <a href={downloadHref} download={doc.fileName} className="px-3 py-1.5 text-xs rounded-lg font-medium text-white" style={{ backgroundColor: PRIMARY_COLOR }}>
                            Download
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 rounded-xl" style={{ backgroundColor: 'rgba(18,76,114,0.02)', border: '2px dashed rgba(18,76,114,0.15)' }}>
                  <span className="text-4xl block mb-3">📁</span>
                  <p className="text-sm" style={{ color: 'rgba(18,76,114,0.5)' }}>No documents uploaded yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'Interview' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold" style={{ color: PRIMARY_COLOR }}>
                  Interview Details
                </h3>
                {permissions.canScheduleInterview &&
                  (candidateDetails.candidate.status === 'Ready For Evaluation' ||
                    candidateDetails.candidate.status === 'Under Evaluation' ||
                    candidateDetails.candidate.status === 'Send Back') && (
                    <button
                      onClick={() => {
                        setCancelInterview(null);
                        setEditInterview(null);
                        setInterviewDate(null);
                        setInterviewTimeFrom('');
                        setInterviewTimeTo('');
                        setInterviewType('');
                        setInterviewBoard([]);
                        setInterviewNotes('');
                        handleInterviewModalOpen();
                      }}
                      className="px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-all"
                      style={{
                        backgroundColor: PRIMARY_COLOR,
                        color: 'white',
                        boxShadow: `0 10px 15px -3px ${SHADOW_COLOR}`
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      Schedule Interview
                    </button>
                  )}
              </div>

              {Array.isArray(candidateDetails?.interviews) && candidateDetails?.interviews.length > 0 ? (
                <div className="space-y-4">
                  {candidateDetails.interviews
                    ?.slice()
                    .sort((a, b) => new Date(a.interview_date) - new Date(b.interview_date))
                    .map((interview, idx) => {
                      const isPastInterview = new Date(interview.interview_time_to) < new Date();
                      const isScheduled = interview.status === 'Interview Scheduled';
                      const statusColor = isScheduled ? '#059669' : interview.status === 'Interview Cancelled' ? '#ef4444' : '#d97706';
                      return (
                        <div key={idx} className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(18,76,114,0.08)' }}>
                          {/* Header */}
                          <div className="flex items-center justify-between px-5 py-3" style={{ backgroundColor: 'rgba(18,76,114,0.04)' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: statusColor, color: '#fff' }}>
                                {idx + 1}
                              </div>
                              <div>
                                <p className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{interview.interviewType || 'Interview'}</p>
                                <p className="text-xs" style={{ color: 'rgba(18,76,114,0.5)' }}>{interview.scheduled_by || ''}</p>
                              </div>
                            </div>
                            <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ backgroundColor: `${statusColor}15`, color: statusColor }}>
                              {interview.status}
                            </span>
                          </div>

                          {/* Details Row */}
                          <div className="flex flex-wrap gap-x-6 gap-y-2 px-5 py-3">
                            <div>
                              <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>Date</p>
                              <p className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{formatLocalDateTime1(interview.interview_date).split(' ')[0]}</p>
                            </div>
                            <div>
                              <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>Time</p>
                              <p className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{formatLocalDateTime1(interview.interview_time_from)} - {formatLocalDateTime1(interview.interview_time_to)}</p>
                            </div>
                            {(interview.interviewBoardMembers || interview.InterviewBoardMembers)?.length > 0 && (
                              <div>
                                <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>Board Members</p>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                  {(interview.interviewBoardMembers || interview.InterviewBoardMembers).map((m, mIdx) => (
                                    <span key={mIdx} className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(18,76,114,0.08)', color: PRIMARY_COLOR }}>
                                      {`${m.users?.first_name || m.users?.First_name || ''} ${m.users?.last_name || m.users?.Last_name || ''}`.trim() || m.users?.email || 'Member'}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {interview.interview_notes && (
                              <div>
                                <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>Notes</p>
                                <p className="text-sm text-gray-500">{interview.interview_notes}</p>
                              </div>
                            )}
                          </div>

                          {!isPastInterview &&
                            (candidateDetails.candidate.status === 'Under Evaluation' ||
                              candidateDetails.candidate.status === 'Interview Scheduled') &&
                            interview.status === 'Interview Scheduled' && (
                              <div className="flex gap-2 mt-4 pt-4 border-t" style={{ borderColor: PRIMARY_LIGHT }}>
                                <button
                                  className="flex-1 px-3 py-2 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2"
                                  style={{
                                    backgroundColor: 'white',
                                    color: ERROR_COLOR,
                                    border: `1px solid ${ERROR_COLOR}`
                                  }}
                                  onClick={() => {
                                    setCancelInterview(interview);
                                    setInterviewDate(new Date(interview.interview_date));
                                    setInterviewTimeFrom(formatTimeOnly(interview.interview_time_from));
                                    setInterviewTimeTo(formatTimeOnly(interview.interview_time_to));
                                    const formattedBoardMembers = interview.interviewBoardMembers?.map((member) => ({
                                      value: member.user_id,
                                      label: `${member.users?.first_name} ${member.users?.last_name}`,
                                      users: member.users,
                                    })) || [];
                                    setInterviewType(interview.interviewType || '');
                                    setInterviewBoard(formattedBoardMembers);
                                    setInterviewNotes(interview.interview_notes || '');
                                    handleInterviewModalOpen();
                                  }}
                                >
                                  <XIcon className="w-4 h-4" />
                                  Cancel
                                </button>
                              </div>
                            )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="text-center py-12 rounded-xl" style={{ 
                  backgroundColor: PRIMARY_LIGHT, 
                  border: `2px dashed ${PRIMARY_MEDIUM}` 
                }}>
                  <svg className="w-16 h-16 mx-auto mb-4" style={{ color: PRIMARY_MEDIUM }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <h4 className="text-lg font-medium mb-2" style={{ color: PRIMARY_COLOR }}>
                    Direct Evaluation
                  </h4>
                  <p className="text-gray-500 mb-4">
                    This candidate is shortlisted for direct evaluation without interview.
                  </p>
                  {permissions.canScheduleInterview && candidateDetails.candidate.status === 'Ready For Evaluation' && (
                    <button
                      onClick={() => {
                        setCancelInterview(null);
                        setEditInterview(null);
                        handleInterviewModalOpen();
                      }}
                      className="px-4 py-2 rounded-lg font-medium transition-all"
                      style={{
                        backgroundColor: PRIMARY_COLOR,
                        color: 'white'
                      }}
                    >
                      Schedule Interview (Optional)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'Evaluation' && Array.isArray(candidateDetails?.evaluations) && candidateDetails?.evaluations.length > 0 && (
            <div className="space-y-6">
              {candidateDetails.evaluations
                .slice()
                .sort((a, b) => new Date(b.evaluation_date || b.created_at || 0) - new Date(a.evaluation_date || a.created_at || 0))
                .map((evaluation, idx) => {
                  const isRecommend = evaluation.hiring_recommendation && !evaluation.hiring_recommendation.toLowerCase().includes('not');
                  const recColor = isRecommend ? '#059669' : '#ef4444';
                  const evalDate = evaluation.evaluation_date || evaluation.created_at || evaluation.interview_date;

                  return (
                    <div key={idx} className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(18,76,114,0.08)' }}>
                      {/* Evaluator Header */}
                      <div className="flex items-center justify-between px-5 py-4" style={{ backgroundColor: 'rgba(18,76,114,0.04)' }}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold" style={{ backgroundColor: PRIMARY_COLOR, color: '#fff' }}>
                            {(evaluation.evaluator_name || evaluation.evaluated_by || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-base font-semibold" style={{ color: PRIMARY_COLOR }}>{evaluation.evaluator_name || evaluation.evaluated_by}</p>
                            <div className="flex items-center gap-2 text-sm" style={{ color: 'rgba(18,76,114,0.5)' }}>
                              {evaluation.evaluator_role && <span>{evaluation.evaluator_role}</span>}
                              {evaluation.evaluator_role && evalDate && <span>·</span>}
                              {evalDate && <span>{new Date(evalDate).toLocaleDateString()}</span>}
                            </div>
                          </div>
                        </div>
                        <span className="text-sm font-bold px-4 py-1.5 rounded-full" style={{ backgroundColor: `${recColor}15`, color: recColor }}>
                          {evaluation.hiring_recommendation}
                        </span>
                      </div>

                      {/* Competency Cards */}
                      {evaluation.competencyEvaluations?.length > 0 && (
                        <div className="px-5 py-4 space-y-3">
                          <p className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>Competency Evaluations</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {evaluation.competencyEvaluations.map((comp, cIdx) => {
                              const ratingColor = (comp.performance_expectation || '').toLowerCase().includes('exceed') ? '#059669' : (comp.performance_expectation || '').toLowerCase().includes('below') ? '#ef4444' : '#d97706';
                              return (
                                <div key={cIdx} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.02)', border: '1px solid rgba(18,76,114,0.06)' }}>
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{comp.competency_name}</span>
                                    <span className="text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: `${ratingColor}12`, color: ratingColor }}>
                                      {comp.performance_expectation || 'N/A'}
                                    </span>
                                  </div>
                                  {comp.comments && (
                                    <p className="text-xs leading-relaxed text-gray-500">{comp.comments}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Additional Comments */}
                      {evaluation.additional_comments && (
                        <div className="px-5 py-3 border-t" style={{ borderColor: 'rgba(18,76,114,0.06)', backgroundColor: 'rgba(18,76,114,0.02)' }}>
                          <p className="text-sm text-gray-500 italic">"{evaluation.additional_comments}"</p>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}

          {activeTab === 'Package' && candidateDetails?.salaryDetails && (() => {
            const basic = Number(candidateDetails.salaryDetails.basicSalary || 0);
            const accom = Number(candidateDetails.salaryDetails.accommodation || 0);
            const trans = Number(candidateDetails.salaryDetails.transportation || 0);
            const total = basic + accom + trans;
            return (
            <div className="space-y-4">
              {/* Total Package Hero */}
              <div className="flex items-center justify-between p-5 rounded-xl" style={{ backgroundColor: 'rgba(18,76,114,0.06)', border: '1px solid rgba(18,76,114,0.1)' }}>
                <div>
                  <p className="text-xs font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>Total Monthly Package</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: PRIMARY_COLOR }}>SAR {total.toLocaleString()}</p>
                </div>
                {permissions.canStartHiringProcess &&
                  (candidateDetails.candidate.status === 'Under Hiring Process' ||
                    candidateDetails.candidate.status === 'Send Back') && (
                    <button onClick={openHiringPackageModal} className="px-4 py-2 text-sm rounded-lg font-medium flex items-center gap-2 transition-all" style={{ backgroundColor: PRIMARY_COLOR, color: 'white' }}>
                      <PencilIcon className="w-4 h-4" /> Edit
                    </button>
                  )}
              </div>

              {/* Breakdown */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(18,76,114,0.08)' }}>
                {[
                  { label: 'Basic Salary', value: basic, percent: total ? Math.round((basic / total) * 100) : 0, color: '#059669' },
                  { label: 'Accommodation Allowance', value: accom, percent: total ? Math.round((accom / total) * 100) : 0, color: '#3b82f6' },
                  { label: 'Transportation Allowance', value: trans, percent: total ? Math.round((trans / total) * 100) : 0, color: '#f59e0b' },
                ].map((item, idx) => (
                  <div key={idx} className={`flex items-center gap-4 px-5 py-4 ${idx < 2 ? 'border-b' : ''}`} style={{ borderColor: 'rgba(18,76,114,0.06)' }}>
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium" style={{ color: PRIMARY_COLOR }}>{item.label}</span>
                        <span className="text-sm font-bold" style={{ color: PRIMARY_COLOR }}>SAR {item.value.toLocaleString()}</span>
                      </div>
                      <div className="w-full h-1.5 rounded-full" style={{ backgroundColor: 'rgba(18,76,114,0.06)' }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${item.percent}%`, backgroundColor: item.color }} />
                      </div>
                    </div>
                    <span className="text-xs font-medium flex-shrink-0" style={{ color: 'rgba(18,76,114,0.4)' }}>{item.percent}%</span>
                  </div>
                ))}
              </div>

              {/* Comments */}
              {candidateDetails.salaryDetails.additionalComments && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.03)', border: '1px solid rgba(18,76,114,0.06)' }}>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(18,76,114,0.5)' }}>Comments</p>
                  <p className="text-sm" style={{ color: PRIMARY_COLOR }}>{candidateDetails.salaryDetails.additionalComments}</p>
                </div>
              )}
            </div>
            );
          })()}

          {activeTab === 'Approvals' && candidateDetails?.candidateApprovals && (
            <div className="space-y-4">
              {/* Approval Timeline */}
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(18,76,114,0.08)' }}>
                {candidateDetails.candidateApprovals.map((approval, idx) => {
                  const isApproved = approval.approval_status === 'Approved';
                  const isRejected = approval.approval_status === 'Rejected' || approval.approval_status === 'Send Back';
                  const statusColor = isApproved ? '#059669' : isRejected ? '#ef4444' : '#d97706';
                  const isLast = idx === candidateDetails.candidateApprovals.length - 1;

                  return (
                    <div key={idx}>
                      <div className="flex items-center gap-4 px-5 py-4">
                        {/* Status Circle */}
                        <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: statusColor, color: '#fff' }}>
                          {isApproved ? '✓' : isRejected ? '✗' : '⏳'}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{approval.approval_level}</span>
                            <span className="text-xs font-medium px-2.5 py-0.5 rounded-full" style={{ backgroundColor: `${statusColor}15`, color: statusColor }}>
                              {approval.approval_status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 text-sm" style={{ color: '#6b7280' }}>
                            <span>{approval.approver}</span>
                            {approval.approver_role && (
                              <>
                                <span>·</span>
                                <span>{approval.approver_role}</span>
                              </>
                            )}
                            {approval.approval_date && (
                              <>
                                <span>·</span>
                                <span>{new Date(approval.approval_date).toLocaleDateString()}</span>
                              </>
                            )}
                          </div>
                          {approval.comments && (
                            <p className="text-sm mt-1 italic" style={{ color: '#9ca3af' }}>"{approval.comments}"</p>
                          )}
                        </div>
                      </div>

                      {/* Connector */}
                      {!isLast && (
                        <div className="ml-[29px]" style={{ borderLeft: `2px solid ${isApproved ? '#059669' : '#e5e7eb'}`, height: '12px' }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Joining date modal (Approved -> Joining) */}
      {showJoiningModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-1" style={{ color: 'rgb(18,76,114)' }}>Set Joining Date</h2>
            <p className="text-sm text-gray-500 mb-4">Select the candidate's date of joining. The candidate will move to <strong>Joining</strong>.</p>
            <label className="block text-sm font-medium mb-1" style={{ color: 'rgb(18,76,114)' }}>Date of Joining</label>
            <input
              type="date"
              value={joiningDateValue}
              onChange={(e) => setJoiningDateValue(e.target.value)}
              className="w-full border rounded-lg p-2 text-sm outline-none"
              style={{ borderColor: 'rgba(18,76,114,0.3)' }}
            />
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowJoiningModal(false)}
                disabled={statusUpdating}
                className="px-4 py-2 rounded-md border text-sm"
                style={{ color: 'rgb(18,76,114)', borderColor: 'rgba(18,76,114,0.3)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmJoining}
                disabled={statusUpdating}
                className="px-4 py-2 rounded-md bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-60"
              >
                {statusUpdating ? 'Saving...' : 'Confirm Joining'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Joined confirmation */}
      {showJoinedConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-1" style={{ color: 'rgb(18,76,114)' }}>Mark as Joined</h2>
            <p className="text-sm text-gray-500 mb-5">
              Mark this candidate as <strong>Joined</strong>? The hiring request will also be set to <strong>Joined</strong>.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowJoinedConfirm(false)}
                disabled={statusUpdating}
                className="px-4 py-2 rounded-md border text-sm"
                style={{ color: 'rgb(18,76,114)', borderColor: 'rgba(18,76,114,0.3)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleMarkJoined}
                disabled={statusUpdating}
                className="px-4 py-2 rounded-md bg-primary-800 text-white text-sm hover:bg-primary-800/80 disabled:opacity-60"
              >
                {statusUpdating ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interview Modal */}
      {isInterviewModalOpen && (
        <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
          <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)' }}>
              <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
                {cancelInterview ? 'Cancel Interview' : editInterview ? 'Reschedule Interview' : 'Schedule Interview'}
              </h2>
              <button onClick={handleInterviewModalClose} className="transition-all rounded-full p-1.5" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Interview Type <span className="text-red-500">*</span></label>
                  <select value={interviewType} onChange={(e) => setInterviewType(e.target.value)} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700">
                    <option value="">-- Select Type --</option>
                    <option value="Online">Online</option>
                    <option value="Offline">Offline</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Interview Date <span className="text-red-500">*</span></label>
                  <DatePicker selected={interviewDate} onChange={date => setInterviewDate(date)} dateFormat="MMMM d, yyyy" className="w-full px-3 py-2.5 border rounded-lg outline-none" placeholderText="Select interview date" minDate={new Date()} />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Start Time <span className="text-red-500">*</span></label>
                  <input type="time" value={interviewTimeFrom} onChange={(e) => setInterviewTimeFrom(e.target.value)} className="w-full px-3 py-2.5 border rounded-lg outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>End Time <span className="text-red-500">*</span></label>
                  <input type="time" value={interviewTimeTo} onChange={(e) => setInterviewTimeTo(e.target.value)} className="w-full px-3 py-2.5 border rounded-lg outline-none" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Interview Board Members <span className="text-red-500">*</span></label>
                <Select isMulti options={interviewBoardOptions} value={interviewBoard} onChange={handleInterviewBoardChange} placeholder="Select board members..." />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Interview Notes</label>
                <textarea value={interviewNotes} onChange={(e) => setInterviewNotes(e.target.value)} rows="3" className="w-full px-3 py-2.5 border rounded-lg outline-none" placeholder="Enter any additional notes..." />
              </div>
              <div className="flex justify-end">
                <button onClick={() => setIsSchedulePopupOpen(true)} disabled={!interviewDate || interviewBoard.length === 0} className="px-4 py-2.5 border rounded-lg transition-all">Check Board Availability</button>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t">
              <button onClick={handleInterviewModalClose} className="px-5 py-2.5 bg-white border rounded-lg">Cancel</button>
              <button onClick={handleScheduleInterview} disabled={statusUpdating} className="px-5 py-2.5 text-white rounded-lg" style={{ backgroundColor: PRIMARY_COLOR }}>
                {statusUpdating ? 'Processing...' : cancelInterview ? 'Confirm Cancel' : editInterview ? 'Confirm Reschedule' : 'Confirm Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ScheduleCheckModal isOpen={isSchedulePopupOpen} onClose={() => setIsSchedulePopupOpen(false)} boardEmails={interviewBoard.map(m => m.users.email)} interviewDate={interviewDate?.toISOString().split("T")[0] || ""} />

      {/* Status Modal ShortList */}
   {isStatusModalOpenShortList && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)' }}>
        <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Confirm Status Change</h2>
        <button onClick={handleStatusModelCloseshortList} className="transition-all rounded-full p-1.5" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="p-6">
        <div className="space-y-2 mb-6">
          <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Select Status <span className="text-red-500">*</span></label>
          <div className="relative">
            <select 
              id="status" 
              value={selectedStatus} 
              onChange={handleInterviweStatusChange} 
              className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none" 
              style={{ 
                borderColor: !selectedStatus ? 'rgba(18, 76, 114, 0.3)' : getStatusBorderColor(selectedStatus), 
                backgroundColor: 'white', 
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, 
                backgroundPosition: 'right 0.5rem center', 
                backgroundRepeat: 'no-repeat', 
                backgroundSize: '1.5em 1.5em', 
                paddingRight: '2.5rem' 
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                e.target.style.borderColor = 'rgb(18, 76, 114)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = !selectedStatus ? 'rgba(18, 76, 114, 0.3)' : getStatusBorderColor(selectedStatus);
              }}
            >
              <option value="">-- Choose Status --</option>
              <option value="Shortlisted For Interview">Shortlisted For Interview (With Interview)</option>
              <option value="Ready For Evaluation">Shortlisted (Direct Evaluation - No Interview)</option>
              <option value="Rejected">Rejected</option>
              {candidateDetails?.candidate?.status !== "On Hold" && <option value="On Hold">On Hold</option>}
            </select>
            {selectedStatus && (
              <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                {getStatusIcon(selectedStatus)}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2 mb-6">
          <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Add Comments</label>
          <textarea 
            id="comments" 
            value={comments} 
            onChange={(e) => setComments(e.target.value)} 
            rows={3} 
            className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 resize-vertical" 
            style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} 
            placeholder="Enter any additional notes or feedback here..."
            onFocus={(e) => {
              e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
              e.target.style.borderColor = 'rgb(18, 76, 114)';
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
            }}
          />
        </div>
        <div className={`flex items-start gap-3 mb-6 p-3 rounded-lg transition-all duration-300 ${selectedStatus ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ backgroundColor: selectedStatus ? getStatusBackgroundColor(selectedStatus) : 'transparent', border: selectedStatus ? `1px solid ${getStatusBorderColor(selectedStatus)}` : 'none' }}>
          <div className="p-1.5 rounded-lg flex-shrink-0" style={{ backgroundColor: selectedStatus ? 'white' : 'transparent' }}>
            {selectedStatus && getStatusIcon(selectedStatus)}
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: selectedStatus ? getStatusTextColor(selectedStatus) : 'rgb(18, 76, 114)' }}>
              {selectedStatus ? `Change to ${selectedStatus === 'Ready For Evaluation' ? 'Ready for Evaluation' : selectedStatus}` : 'Select a status'}
            </p>
            <p className="text-sm mt-1" style={{ color: selectedStatus ? getStatusTextColor(selectedStatus) : '#9CA3AF' }}>
              {selectedStatus ? 'Are you sure you want to change the candidate\'s status?' : 'Please select a status to continue'}
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row justify-end gap-3 p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, transparent, rgba(18, 76, 114, 0.05))' }}>
        <button 
          onClick={handleStatusModelCloseshortList} 
          className="px-5 py-2.5 bg-white border rounded-lg transition-all font-medium" 
          style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.3)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          Cancel
        </button>
        <button 
          onClick={() => handleStatusClick(selectedStatus)} 
          disabled={statusUpdating || !selectedStatus} 
          className="px-5 py-2.5 text-white font-medium rounded-lg transition-all" 
          style={{ 
            backgroundColor: !selectedStatus || statusUpdating ? 'rgba(18, 76, 114, 0.5)' : getStatusButtonColor(selectedStatus), 
            cursor: !selectedStatus || statusUpdating ? 'not-allowed' : 'pointer',
            boxShadow: !selectedStatus || statusUpdating ? 'none' : '0 10px 15px -3px rgba(18, 76, 114, 0.2)'
          }}
          onMouseEnter={(e) => {
            if (selectedStatus && !statusUpdating) {
              e.currentTarget.style.opacity = '0.9';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedStatus && !statusUpdating) {
              e.currentTarget.style.opacity = '1';
            }
          }}
        >
          {statusUpdating ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Updating...
            </span>
          ) : (
            'Yes, Change Status'
          )}
        </button>
      </div>
    </div>
  </div>
)}
   {isStatusModalOpen && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)' }}>
        <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Confirm Status Change</h2>
        <button onClick={handleStatusModelClose} className="transition-all rounded-full p-1.5" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="p-6">
        <div className="space-y-2 mb-6">
          <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Select Status <span className="text-red-500">*</span></label>
          <div className="relative">
            <select id="status" value={selectedStatus} onChange={handleInterviweStatusChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none" style={{ borderColor: !selectedStatus ? 'rgba(18, 76, 114, 0.3)' : getHiringManagerStatusBorderColor(selectedStatus), backgroundColor: 'white', backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}>
              <option value="">-- Choose Status --</option>
              <option value="Approved By Hiring Manager">Approved</option>
              <option value="Rejected By Hiring Manager">Rejected</option>
            </select>
            {selectedStatus && <div className="absolute right-10 top-1/2 transform -translate-y-1/2">{getHiringManagerStatusIcon(selectedStatus)}</div>}
          </div>
        </div>
        <div className="space-y-2 mb-6">
          <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Add Comments</label>
          <textarea id="comments" value={comments} onChange={(e) => setComments(e.target.value)} rows={3} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 resize-vertical" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} placeholder="Enter any additional notes or feedback here..." />
        </div>
        <div className={`flex items-start gap-3 mb-6 p-3 rounded-lg transition-all duration-300 ${selectedStatus ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ backgroundColor: selectedStatus ? getHiringManagerStatusBackgroundColor(selectedStatus) : 'transparent', border: selectedStatus ? `1px solid ${getHiringManagerStatusBorderColor(selectedStatus)}` : 'none' }}>
          <div className="p-1.5 rounded-lg flex-shrink-0" style={{ backgroundColor: selectedStatus ? 'white' : 'transparent' }}>{selectedStatus && getHiringManagerStatusIcon(selectedStatus)}</div>
          <div><p className="text-sm font-medium" style={{ color: selectedStatus ? getHiringManagerStatusTextColor(selectedStatus) : 'rgb(18, 76, 114)' }}>{selectedStatus ? getHiringManagerStatusDisplayName(selectedStatus) : 'Select a status'}</p><p className="text-sm mt-1" style={{ color: selectedStatus ? getHiringManagerStatusTextColor(selectedStatus) : '#9CA3AF' }}>{selectedStatus ? 'Are you sure you want to change the candidate\'s status?' : 'Please select a status to continue'}</p></div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row justify-end gap-3 p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, transparent, rgba(18, 76, 114, 0.05))' }}>
        <button onClick={handleStatusModelClose} className="px-5 py-2.5 bg-white border rounded-lg transition-all font-medium" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.3)' }}>Cancel</button>
        <button onClick={() => handleStatusClick(selectedStatus)} disabled={statusUpdating || !selectedStatus} className="px-5 py-2.5 text-white font-medium rounded-lg transition-all" style={{ backgroundColor: !selectedStatus || statusUpdating ? 'rgba(18, 76, 114, 0.5)' : getHiringManagerStatusButtonColor(selectedStatus), cursor: !selectedStatus || statusUpdating ? 'not-allowed' : 'pointer' }}>
          {statusUpdating ? <span className="flex items-center justify-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Updating...</span> : 'Yes, Change Status'}
        </button>
      </div>
    </div>
  </div>
)}

 {isStatusModalOpenInitial && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)' }}>
        <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Confirm Status Change</h2>
        <button onClick={handleStatusModelClosesinitial} className="transition-all rounded-full p-1.5" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="p-6">
        <div className="space-y-2 mb-6">
          <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Select Status <span className="text-red-500">*</span></label>
          <div className="relative">
            <select 
              id="status" 
              value={selectedStatus} 
              onChange={handleInterviweStatusChange} 
              className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none" 
              style={{ 
                borderColor: !selectedStatus ? 'rgba(18, 76, 114, 0.3)' : getInitialStatusBorderColor(selectedStatus), 
                backgroundColor: 'white',
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.5em 1.5em',
                paddingRight: '2.5rem'
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                e.target.style.borderColor = 'rgb(18, 76, 114)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.borderColor = !selectedStatus ? 'rgba(18, 76, 114, 0.3)' : getInitialStatusBorderColor(selectedStatus);
              }}
            >
              <option value="">-- Choose Status --</option>
              <option value="Send For Initial Approval">Send For Initial Approval</option>
              <option value="Rejected By Recruiter">Rejected</option>
            </select>
            {selectedStatus && (
              <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
                {getInitialStatusIcon(selectedStatus)}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2 mb-6">
          <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Add Comments</label>
          <textarea 
            value={comments} 
            onChange={(e) => setComments(e.target.value)} 
            rows={3} 
            className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 resize-vertical" 
            style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} 
            placeholder="Enter any additional notes or feedback here..."
            onFocus={(e) => {
              e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
              e.target.style.borderColor = 'rgb(18, 76, 114)';
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = 'none';
              e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
            }}
          />
        </div>
        <div className={`flex items-start gap-3 mb-6 p-3 rounded-lg transition-all duration-300 ${selectedStatus ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ backgroundColor: selectedStatus ? getInitialStatusBackgroundColor(selectedStatus) : 'transparent', border: selectedStatus ? `1px solid ${getInitialStatusBorderColor(selectedStatus)}` : 'none' }}>
          <div className="p-1.5 rounded-lg flex-shrink-0" style={{ backgroundColor: selectedStatus ? 'white' : 'transparent' }}>
            {selectedStatus && getInitialStatusIcon(selectedStatus)}
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: selectedStatus ? getInitialStatusTextColor(selectedStatus) : 'rgb(18, 76, 114)' }}>
              {selectedStatus ? getInitialStatusDisplayName(selectedStatus) : 'Select a status'}
            </p>
            <p className="text-sm mt-1" style={{ color: selectedStatus ? getInitialStatusTextColor(selectedStatus) : '#9CA3AF' }}>
              {selectedStatus ? 'Are you sure you want to change the candidate\'s status?' : 'Please select a status to continue'}
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row justify-end gap-3 p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, transparent, rgba(18, 76, 114, 0.05))' }}>
        <button 
          onClick={handleStatusModelClosesinitial} 
          className="px-5 py-2.5 bg-white border rounded-lg transition-all font-medium" 
          style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.3)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          Cancel
        </button>
        <button 
          onClick={() => handleStatusClick(selectedStatus)} 
          disabled={statusUpdating || !selectedStatus} 
          className="px-5 py-2.5 text-white font-medium rounded-lg transition-all" 
          style={{ 
            backgroundColor: !selectedStatus || statusUpdating ? 'rgba(18, 76, 114, 0.5)' : getInitialStatusButtonColor(selectedStatus), 
            cursor: !selectedStatus || statusUpdating ? 'not-allowed' : 'pointer',
            boxShadow: !selectedStatus || statusUpdating ? 'none' : '0 10px 15px -3px rgba(18, 76, 114, 0.2)'
          }}
          onMouseEnter={(e) => {
            if (selectedStatus && !statusUpdating) {
              e.currentTarget.style.opacity = '0.9';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedStatus && !statusUpdating) {
              e.currentTarget.style.opacity = '1';
            }
          }}
        >
          {statusUpdating ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Updating...
            </span>
          ) : (
            'Yes, Change Status'
          )}
        </button>
      </div>
    </div>
  </div>
)}

{showHiringModal && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)' }}>
        <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Finalize Candidate Evaluation</h2>
        <button onClick={handleHiringModalClose} className="transition-all rounded-full p-1.5" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="p-6 space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Select Final Status <span className="text-red-500">*</span></label>
          <select value={finalStatus} onChange={(e) => setFinalStatus(e.target.value)} className="w-full px-3 py-2.5 border rounded-lg outline-none">
            <option value="">-- Choose Status --</option>
            <option value="ShortListed">ShortListed</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
            Comments <span className="text-sm font-normal" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>(Optional)</span>
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Enter any additional notes or feedback..."
            className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 resize-vertical"
            style={{ borderColor: 'rgba(18, 76, 114, 0.3)' }}
          />
        </div>
        <div className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#f59e0b' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.342 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          <div><p className="text-sm font-medium" style={{ color: '#f59e0b' }}>Final Decision</p><p className="text-xs mt-1" style={{ color: 'rgba(245, 158, 11, 0.8)' }}>This action will finalize the candidate's evaluation and cannot be undone.</p></div>
        </div>
      </div>
      <div className="flex justify-end gap-3 p-6 border-t">
        <button onClick={handleHiringModalClose} className="px-5 py-2.5 bg-white border rounded-lg">Cancel</button>
        <button onClick={handleStatusSubmit} disabled={!finalStatus} className="px-5 py-2.5 text-white rounded-lg" style={{ backgroundColor: finalStatus === 'ShortListed' ? '#22c55e' : '#ef4444' }}>Confirm Decision</button>
      </div>
    </div>
  </div>
)}

{showHiringPackageModal && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)' }}>
        <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Hiring Package Details</h2>
        <button onClick={handleHiringPackageModalClose} className="transition-all rounded-full p-1.5" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Basic Salary <span className="text-red-500">*</span></label><input type="number" placeholder="Enter Basic Salary" value={salaryDetails.basicSalary} onChange={e => setSalaryDetails(prev => ({ ...prev, basicSalary: e.target.value }))} className="w-full px-3 py-2.5 border rounded-lg outline-none" /></div>
          <div><label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Accommodation Allowance <span className="text-red-500">*</span></label><input type="number" placeholder="Enter Accommodation Allowance" value={salaryDetails.accommodation} onChange={e => setSalaryDetails(prev => ({ ...prev, accommodation: e.target.value }))} className="w-full px-3 py-2.5 border rounded-lg outline-none" /></div>
          <div><label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Transportation Allowance <span className="text-red-500">*</span></label><input type="number" placeholder="Enter Transportation Allowance" value={salaryDetails.transportation} onChange={e => setSalaryDetails(prev => ({ ...prev, transportation: e.target.value }))} className="w-full px-3 py-2.5 border rounded-lg outline-none" /></div>
          <div><label className="block text-sm font-medium" style={{ color: '#22c55e' }}>Total Monthly Package</label><input type="number" value={Number(salaryDetails.basicSalary || 0) + Number(salaryDetails.accommodation || 0) + Number(salaryDetails.transportation || 0)} readOnly className="w-full px-3 py-2.5 border rounded-lg outline-none bg-gray-50" /></div>
        </div>
        <div><label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Additional Comments</label><textarea rows={3} placeholder="Any additional hiring notes..." value={salaryDetails.additionalComments} onChange={e => setSalaryDetails(prev => ({ ...prev, additionalComments: e.target.value }))} className="w-full px-3 py-2.5 border rounded-lg outline-none" /></div>
      </div>
      <div className="flex justify-end gap-3 p-6 border-t">
        <button onClick={handleHiringPackageModalClose} className="px-5 py-2.5 bg-white border rounded-lg">Cancel</button>
        <button onClick={handleHiringPackageModalSubmit} disabled={!salaryDetails.basicSalary || !salaryDetails.accommodation || !salaryDetails.transportation} className="px-5 py-2.5 text-white rounded-lg" style={{ backgroundColor: '#22c55e' }}>Submit Package</button>
      </div>
    </div>
  </div>
)}

{showSendForApprovalModal && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)' }}>
        <div className="flex items-center gap-3"><div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}><svg className="w-5 h-5" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg></div><h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Send For Approval</h2></div>
        <button onClick={handleSendforApprovalModalClose} className="transition-all rounded-full p-1.5"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
      </div>
      <div className="p-6 space-y-6">
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.03)', border: '1px solid rgba(18, 76, 114, 0.1)' }}><svg className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><div><p className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Request Approval</p><p className="text-sm mt-1" style={{ color: 'rgba(18, 76, 114, 0.8)' }}>Are you sure you want to send this candidate's profile for approval?</p></div></div>
        <div><label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Add Comments <span className="text-sm font-normal" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>(Optional)</span></label><textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} className="w-full px-3 py-2.5 border rounded-lg outline-none" placeholder="Enter any additional notes..." /></div>
      </div>
      <div className="flex justify-end gap-3 p-6 border-t">
        <button onClick={handleSendforApprovalModalClose} className="px-5 py-2.5 bg-white border rounded-lg">Cancel</button>
        <button onClick={handleSendForApprvalSubmit} className="px-5 py-2.5 text-white rounded-lg" style={{ backgroundColor: PRIMARY_COLOR }}>Send For Approval</button>
      </div>
    </div>
  </div>
)}

{showApprovalModal && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)' }}>
        <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Approval Request</h2>
        <button onClick={handleApprovalModalClose} className="transition-all rounded-full p-1.5"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
      </div>
      <div className="p-6">
        <div className="flex items-start gap-3 mb-6 p-3 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.05)' }}><div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}><svg className="w-5 h-5" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div><div><p className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Confirm Approval Action</p><p className="text-sm text-gray-600 mt-1">Are you sure you want to approve this candidate?</p></div></div>
        <div className="space-y-2 mb-6"><label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Approval Status <span className="text-red-500">*</span></label><select value={FinalApprovalStatus} onChange={(e) => setFinalApprovalStatus(e.target.value)} className="w-full px-3 py-2.5 border rounded-lg outline-none"><option value="">-- Choose Status --</option><option value="Approved">Approve</option><option value="Send Back">Send Back</option><option value="Rejected">Rejected</option></select></div>
        <div className="space-y-2 mb-6"><label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Add Comments</label><textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} className="w-full px-3 py-2.5 border rounded-lg outline-none" placeholder="Enter any additional notes..." /></div>
      </div>
      <div className="flex justify-end gap-3 p-6 border-t">
        <button onClick={handleApprovalModalClose} className="px-5 py-2.5 bg-white border rounded-lg">Cancel</button>
        <button onClick={handleApprvalSubmit} disabled={!FinalApprovalStatus} className="px-5 py-2.5 text-white rounded-lg" style={{ backgroundColor: !FinalApprovalStatus ? 'rgba(18, 76, 114, 0.5)' : getStatusButtonColor(FinalApprovalStatus) }}>{getStatusButtonText(FinalApprovalStatus)}</button>
      </div>
    </div>
  </div>
)}

 {isEditModalOpen && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)' }}>
        <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>Edit Candidate Information</h2>
        <button onClick={handleEditClose} className="transition-all rounded-full p-1.5" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-6">
          <div className="flex justify-between mb-8 relative">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex flex-col items-center relative z-10">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${currentStep >= step ? 'text-white' : 'text-gray-400 border'}`} style={{ backgroundColor: currentStep >= step ? 'rgb(18, 76, 114)' : 'white', borderColor: currentStep >= step ? 'rgb(18, 76, 114)' : 'rgba(18, 76, 114, 0.3)' }}>
                  {step}
                </div>
                <span className="text-xs mt-2 font-medium" style={{ color: currentStep >= step ? 'rgb(18, 76, 114)' : '#9CA3AF' }}>
                  {step === 1 && 'Personal'}
                  {step === 2 && 'Experience'}
                  {step === 3 && 'Documents'}
                  {step === 4 && 'Review'}
                </span>
              </div>
            ))}
            <div className="absolute top-4 left-8 right-8 h-0.5 z-0" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }} />
          </div>

          {currentStep === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Full Name <span className="text-red-500">*</span></label>
                <input type="text" name="fullName" value={formData.full_name} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Email <span className="text-red-500">*</span></label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Phone Number</label>
                <input type="tel" name="phone_number" value={formData.phone_number} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Date of Birth</label>
                <input type="date" name="date_of_birth" value={formatDateToInput(formData.date_of_birth)} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} max={new Date().toISOString().split("T")[0]} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Gender</label>
                <select name="gender" value={formData.gender} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white', backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Source of CV</label>
                <select name="source_of_cv" value={formData.source_of_cv} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white', backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }}>
                  <option value="">Select Source</option>
                  <option value="Third party">Third party</option>
                  <option value="Reference">Reference</option>
                  <option value="HR">HR</option>
                </select>
              </div>
             <div className="space-y-2">
  <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Nationality</label>
  <div className="relative">
    <input 
      type="text" 
      placeholder="Search nationality..." 
      value={searchTerm || formData.nationality || ''} 
      onChange={(e) => { 
        setSearchTerm(e.target.value); 
        setIsDropdownVisible(true);
      }} 
      onFocus={() => setIsDropdownVisible(true)}
      className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" 
      style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} 
    />
    {isDropdownVisible && (searchTerm || !formData.nationality) && (
      <div className="absolute left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-90 " style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
        <NationalityList 
          nationalities={filteredNationalities} 
          onSelectNationality={(nationality) => {
            handleSelectNationality(nationality);
            setIsDropdownVisible(false);
          }} 
          selectedNationality={formData.nationality} 
        />
      </div>
    )}
  </div>
</div>
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input type="checkbox" id="is_citizen" name="is_citizen" checked={formData.is_citizen} onChange={handleChange} className="sr-only" />
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${formData.is_citizen ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'}`} style={{ borderColor: formData.is_citizen ? 'rgb(18, 76, 114)' : 'rgba(18, 76, 114, 0.3)' }}>
                      {formData.is_citizen && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                    </div>
                  </div>
                  <span className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Is Citizen</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input type="checkbox" id="is_transfer_ownership" name="is_transfer_ownership" checked={formData.is_transfer_ownership} onChange={handleChange} className="sr-only" />
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${formData.is_transfer_ownership ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'}`} style={{ borderColor: formData.is_transfer_ownership ? 'rgb(18, 76, 114)' : 'rgba(18, 76, 114, 0.3)' }}>
                      {formData.is_transfer_ownership && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                    </div>
                  </div>
                  <span className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Is Transfer Ownership</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input type="checkbox" id="is_new_visa" name="is_new_visa" checked={formData.is_new_visa} onChange={handleChange} className="sr-only" />
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${formData.is_new_visa ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'}`} style={{ borderColor: formData.is_new_visa ? 'rgb(18, 76, 114)' : 'rgba(18, 76, 114, 0.3)' }}>
                      {formData.is_new_visa && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                    </div>
                  </div>
                  <span className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Is New Visa Required</span>
                </label>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Company Name</label>
                <input type="text" name="company_name" value={formData.company_name} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Experience (Years)</label>
                <input type="number" name="experience_years" value={formData.experience_years} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Experience in KSA (Years)</label>
                <input type="number" name="experience_years_at_ksa" value={formData.experience_years_at_ksa} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Last Job Title</label>
                <input type="text" name="last_job_title" value={formData.last_job_title} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Last Salary</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">SAR</span>
                  <input type="number" name="last_salary" value={formData.last_salary} onChange={handleInputChange} className="w-full pl-12 pr-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Expected Salary</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">SAR</span>
                  <input type="number" name="expected_salary" value={formData.expected_salary} onChange={handleInputChange} className="w-full pl-12 pr-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Highest Degree</label>
                <input type="text" name="highest_degree" value={formData.highest_degree} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Major</label>
                <input type="text" name="major" value={formData.major} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Institute Name</label>
                <input type="text" name="institute_name" value={formData.institute_name} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }} />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Completion Year</label>
                <select name="completion_year" value={formData.completion_year} onChange={handleInputChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition text-gray-700 appearance-none" style={{ borderColor: 'rgba(18, 76, 114, 0.3)', backgroundColor: 'white', backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }} onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)'; e.target.style.borderColor = 'rgb(18, 76, 114)'; }} onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)'; }}>
                  <option value="">Select year</option>
                  {Array.from({ length: new Date().getFullYear() - 1950 + 1 }, (_, i) => 1950 + i).map((year) => (<option key={year} value={year}>{year}</option>))}
                </select>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>Upload Document (PDF/Image)</label>
                <div className="relative">
                  <input type="file" onChange={handleFileChange} accept="application/pdf,image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                  <div className="px-4 py-4 border-2 border-dashed rounded-lg transition-all text-center" style={{ borderColor: formData.Documents?.fileName ? 'rgba(18, 76, 114, 0.3)' : 'rgba(18, 76, 114, 0.2)', backgroundColor: 'white' }}>
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                        <svg className="w-6 h-6" style={{ color: 'rgb(18, 76, 114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>{formData.Documents?.fileName || 'Click to upload document'}</p>
                        <p className="text-xs text-gray-500 mt-1">{formData.Documents?.fileName ? `(${(formData.Documents.size / 1024).toFixed(2)} KB)` : 'Supports: PDF, PNG, JPG (Max 5MB)'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-5 border rounded-xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
                  <h4 className="font-bold text-lg mb-4 pb-2 border-b" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.1)' }}>Personal Information</h4>
                  <div className="space-y-3">
                    {[{ label: 'Full Name', value: formData.full_name }, { label: 'Email', value: formData.email }, { label: 'Phone Number', value: formData.phone_number }, { label: 'Date of Birth', value: formData.date_of_birth }, { label: 'Gender', value: formData.gender }, { label: 'Nationality', value: formData.nationality }].map((item, idx) => (
                      <div key={idx} className="flex justify-between items-start py-2 border-b last:border-b-0" style={{ borderColor: 'rgba(18, 76, 114, 0.05)' }}>
                        <span className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>{item.label}</span>
                        <span className="text-sm text-gray-600 text-right max-w-[200px] break-words">{item.value || 'Not provided'}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-5 border rounded-xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
                  <h4 className="font-bold text-lg mb-4 pb-2 border-b" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.1)' }}>Experience & Education</h4>
                  <div className="space-y-3">
                    {[{ label: 'Company Name', value: formData.company_name }, { label: 'Experience (Years)', value: formData.experience_years }, { label: 'Experience in KSA', value: formData.experience_years_at_ksa }, { label: 'Last Job Title', value: formData.last_job_title }, { label: 'Last Salary', value: formData.last_salary ? `SAR ${formData.last_salary}` : '' }, { label: 'Expected Salary', value: formData.expected_salary ? `SAR ${formData.expected_salary}` : '' }].map((item, idx) => (
                      <div key={idx} className="flex justify-between items-start py-2 border-b last:border-b-0" style={{ borderColor: 'rgba(18, 76, 114, 0.05)' }}>
                        <span className="text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>{item.label}</span>
                        <span className="text-sm text-gray-600 text-right max-w-[200px] break-words">{item.value || 'Not provided'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-between items-center p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, transparent, rgba(18, 76, 114, 0.05))' }}>
        <button onClick={currentStep > 1 ? prevStep : handleEditClose} className="px-5 py-2.5 bg-white border rounded-lg transition-all font-medium" style={{ color: 'rgb(18, 76, 114)', borderColor: 'rgba(18, 76, 114, 0.3)' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}>
          {currentStep > 1 ? 'Previous' : 'Cancel'}
        </button>
        <button onClick={currentStep < 4 ? nextStep : handleEditCandidate} disabled={statusUpdating} className="px-5 py-2.5 text-white font-medium rounded-lg transition-all" style={{ backgroundColor: statusUpdating ? 'rgba(18, 76, 114, 0.5)' : 'rgb(18, 76, 114)', cursor: statusUpdating ? 'not-allowed' : 'pointer', boxShadow: statusUpdating ? 'none' : '0 10px 15px -3px rgba(18, 76, 114, 0.2)' }} onMouseEnter={(e) => { if (!statusUpdating) { e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.9)'; } }} onMouseLeave={(e) => { if (!statusUpdating) { e.currentTarget.style.backgroundColor = 'rgb(18, 76, 114)'; } }}>
          {currentStep < 4 ? 'Next' : (statusUpdating ? 'Updating...' : 'Submit')}
        </button>
      </div>
    </div>
  </div>
)}

{isDocumentModalOpen && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.15)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', borderWidth: '1px' }}>
      <div className="flex items-center justify-between p-5 sm:p-6 border-b" style={{ borderColor: 'rgba(18, 76, 114, 0.08)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.04), transparent)' }}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.08)' }}>
            <svg className="w-5 h-5" style={{ color: PRIMARY_COLOR }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold" style={{ color: PRIMARY_COLOR }}>Upload Document</h2>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>PDF, PNG, JPG supported</p>
          </div>
        </div>
        <button onClick={closeDocModal} className="flex-shrink-0 p-1.5 rounded-lg" style={{ color: 'rgba(18, 76, 114, 0.5)', backgroundColor: 'rgba(18, 76, 114, 0.05)' }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="p-5 sm:p-6 space-y-5">
        <div className="space-y-2">
          <label className="block text-sm font-medium" style={{ color: PRIMARY_COLOR }}>Process Type <span className="text-red-500">*</span></label>
          <select onChange={handleProcessTypeChange} value={processType} className="w-full px-3 py-2.5 border rounded-lg outline-none transition" style={{ borderColor: 'rgba(18, 76, 114, 0.3)' }}>
            <option value="">Select Process Type</option>
            <option value="Resume">Resume</option>
            <option value="Identification">Identification (Iqama or National ID)</option>
            <option value="Profile Photo">Profile Photo</option>
            <option value="Pay Slip">Pay Slip</option>
            <option value="Passport">Passport</option>
            <option value="Bank Details">Bank Details</option>
            <option value="Declaration Form">Declaration Form</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {processType === 'Other' && (
          <div className="space-y-2">
            <label className="block text-sm font-medium" style={{ color: PRIMARY_COLOR }}>Custom Process Type <span className="text-red-500">*</span></label>
            <input type="text" value={customProcessType} onChange={handleCustomProcessTypeChange} className="w-full px-3 py-2.5 border rounded-lg outline-none transition" style={{ borderColor: 'rgba(18, 76, 114, 0.3)' }} placeholder="Enter custom process type" />
          </div>
        )}
        <div className="space-y-2">
          <label className="block text-sm font-medium" style={{ color: PRIMARY_COLOR }}>Document <span className="text-red-500">*</span></label>
          <label className="flex flex-col items-center justify-center w-full py-8 border-2 border-dashed rounded-xl cursor-pointer transition-all hover:border-solid" style={{ borderColor: docFile ? '#059669' : 'rgba(18, 76, 114, 0.3)', backgroundColor: docFile ? 'rgba(5, 150, 105, 0.05)' : 'rgba(18, 76, 114, 0.02)' }}>
            <input type="file" onChange={handleDocFileChange} className="hidden" accept="application/pdf,image/*" />
            {docFile ? (
              <div className="flex items-center gap-3">
                <svg className="w-8 h-8" style={{ color: '#059669' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium" style={{ color: '#059669' }}>{docFile.name}</p>
                  <p className="text-xs text-gray-500">{(docFile.size / 1024).toFixed(1)} KB — Click to change</p>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <svg className="w-10 h-10 mx-auto mb-2" style={{ color: 'rgba(18, 76, 114, 0.3)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm font-medium" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>Click to browse files</p>
                <p className="text-xs mt-1" style={{ color: 'rgba(18, 76, 114, 0.4)' }}>PDF, PNG, JPG up to 10MB</p>
              </div>
            )}
          </label>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 p-5 sm:p-6 border-t rounded-b-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.08)', backgroundColor: 'rgba(18, 76, 114, 0.02)' }}>
        <button onClick={closeDocModal} className="flex-1 px-4 py-3 bg-white border rounded-xl font-medium" style={{ color: PRIMARY_COLOR, borderColor: 'rgba(18, 76, 114, 0.2)' }}>Cancel</button>
        <button onClick={handleUploadDocument} className="flex-1 px-4 py-3 text-white font-medium rounded-xl transition-all" style={{ backgroundColor: PRIMARY_COLOR }}>Upload Document</button>
      </div>
    </div>
  </div>
)}

{showOfferLetterModalOpen && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b rounded-t-2xl" style={{ borderColor: 'rgba(18, 76, 114, 0.1)', background: 'linear-gradient(to right, rgba(18, 76, 114, 0.05), transparent)' }}>
        <div className="flex items-center gap-3"><div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}><svg className="w-6 h-6" style={{ color: PRIMARY_COLOR }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></div><h2 className="text-xl font-semibold" style={{ color: PRIMARY_COLOR }}>Offer Letter Preview</h2></div>
        <button onClick={handleOfferLetterModalClose} className="transition-all rounded-full p-1.5"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
      </div>
      <div className="flex-1 overflow-y-auto p-6 bg-gray-50"><div id="offer-letter-html" className="bg-white p-8 rounded-lg shadow-lg max-w-3xl mx-auto" style={{ border: '1px solid rgba(18, 76, 114, 0.1)', minHeight: '500px' }} dangerouslySetInnerHTML={{ __html: offerLetterHtml }} /></div>
      <div className="flex justify-end gap-3 p-6 border-t">
        <button onClick={handleOfferLetterModalClose} className="px-5 py-2.5 bg-white border rounded-lg">Close</button>
        <button onClick={handleDownloadPdf} className="px-5 py-2.5 text-white rounded-lg flex items-center gap-2" style={{ backgroundColor: PRIMARY_COLOR }}><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>Download PDF</button>
      </div>
    </div>
  </div>
)}

{isModalOpen && selectedDocument && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18, 76, 114, 0.2)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-full sm:max-w-3xl md:max-w-4xl lg:max-w-5xl h-[90vh]">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b flex-shrink-0" style={{ borderColor: 'rgba(18,76,114,0.08)' }}>
        <div className="flex items-center gap-3">
          <span className="text-xl">{selectedDocument.mimeType?.includes('pdf') ? '📄' : selectedDocument.mimeType?.startsWith('image/') ? '🖼️' : '📎'}</span>
          <div>
            <h2 className="text-base font-semibold" style={{ color: PRIMARY_COLOR }}>{selectedDocument.process_type === 'Other' ? selectedDocument.other_Process_type : selectedDocument.process_type || 'Document Preview'}</h2>
            <p className="text-xs" style={{ color: 'rgba(18,76,114,0.5)' }}>{selectedDocument.fileName || 'file'}</p>
          </div>
        </div>
        <button onClick={handleCloseModal} className="p-1.5 rounded-lg" style={{ color: 'rgba(18,76,114,0.5)', backgroundColor: 'rgba(18,76,114,0.05)' }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-hidden p-4">
        <div className="w-full h-full border rounded-lg overflow-hidden" style={{ borderColor: 'rgba(18,76,114,0.1)' }}>
          {selectedDocument.previewUrl ? (
            selectedDocument.mimeType?.startsWith("application/pdf") ? (
              <iframe src={selectedDocument.previewUrl} title="PDF Document" className="w-full h-full border-0" />
            ) : selectedDocument.mimeType?.startsWith("image/") ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-50">
                <img src={selectedDocument.previewUrl} alt="Preview" className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full"><p className="text-gray-500">Unsupported file type.</p></div>
            )
          ) : (
            <div className="flex items-center justify-center h-full"><p className="text-gray-500">Preview not available. Use download instead.</p></div>
          )}
        </div>
      </div>
      {/* Footer */}
      <div className="flex justify-end gap-3 px-5 py-3 border-t flex-shrink-0" style={{ borderColor: 'rgba(18,76,114,0.08)' }}>
        <a href={selectedDocument.isAzure ? (selectedDocument.blobUrl || selectedDocument.BlobUrl) : `data:application/octet-stream;base64,${selectedDocument.fileBase64}`}
          download={selectedDocument.fileName}
          className="px-4 py-2 text-sm rounded-lg font-medium text-white" style={{ backgroundColor: PRIMARY_COLOR }}>
          Download
        </a>
        <button onClick={handleCloseModal} className="px-4 py-2 text-sm bg-white border rounded-lg font-medium" style={{ color: PRIMARY_COLOR, borderColor: 'rgba(18,76,114,0.2)' }}>Close</button>
      </div>
    </div>
  </div>
)}

{/* More Info Popup — two tabs */}
{showMoreInfoPopup && (
  <div className="fixed inset-0 backdrop-blur-sm flex justify-center items-center p-4 z-50" style={{ backgroundColor: 'rgba(18,76,114,0.15)' }}>
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] flex flex-col" style={{ border: '1px solid rgba(18,76,114,0.1)' }}>
      {/* Header + Tabs */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: '1px solid rgba(18,76,114,0.08)' }}>
          <h2 className="text-base font-semibold" style={{ color: PRIMARY_COLOR }}>Candidate Information</h2>
          <button onClick={() => setShowMoreInfoPopup(false)} className="p-1.5 rounded-lg" style={{ color: 'rgba(18,76,114,0.5)', backgroundColor: 'rgba(18,76,114,0.05)' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="flex gap-0 px-5 pt-3" style={{ borderBottom: '1px solid rgba(18,76,114,0.08)' }}>
          {['personal', 'experience', 'hiringRequest'].map(tab => (
            <button key={tab} onClick={() => setMoreInfoTab(tab)}
              className="px-4 py-2 text-sm font-medium transition-all -mb-px"
              style={{
                color: moreInfoTab === tab ? PRIMARY_COLOR : 'rgba(18,76,114,0.5)',
                borderBottom: moreInfoTab === tab ? `2px solid ${PRIMARY_COLOR}` : '2px solid transparent',
              }}>
              {tab === 'personal' ? 'Personal' : tab === 'experience' ? 'Experience' : 'Hiring Request'}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {moreInfoTab === 'personal' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: 'Full Name', value: candidateDetails.candidate.full_name || '—' },
              { label: 'Email', value: candidateDetails.candidate.email || '—' },
              { label: 'Phone Number', value: candidateDetails.candidate.phone_number || '—' },
              { label: 'Date of Birth', value: candidateDetails.candidate.date_of_birth ? new Date(candidateDetails.candidate.date_of_birth).toLocaleDateString() : '—' },
              { label: 'Gender', value: candidateDetails.candidate.gender || '—' },
              { label: 'Nationality', value: candidateDetails.candidate.nationality || '—' },
              { label: 'Residency Status', value: candidateDetails.candidate.is_citizen ? 'Citizen' : candidateDetails.candidate.is_transfer_ownership ? 'Transfer Ownership' : candidateDetails.candidate.is_new_visa ? 'New Visa' : '—' },
              { label: 'Source of CV', value: candidateDetails.candidate.source_of_cv || '—' },
              { label: 'Application Date', value: candidateDetails.candidate.application_date ? new Date(candidateDetails.candidate.application_date).toLocaleDateString() : '—' },
              { label: 'Status', value: candidateDetails.candidate.status || '—' },
            ].map((item, idx) => (
              <div key={idx} className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.03)' }}>
                <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>{item.label}</p>
                <p className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{item.value}</p>
              </div>
            ))}
            {candidateDetails.candidate.descriptions && (
              <div className="col-span-2 md:col-span-3 p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.03)' }}>
                <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>Notes</p>
                <p className="text-sm leading-relaxed" style={{ color: PRIMARY_COLOR }}>{candidateDetails.candidate.descriptions}</p>
              </div>
            )}
          </div>
        )}

        {moreInfoTab === 'experience' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: 'Company Name', value: candidateDetails.candidate.company_name || '—' },
              { label: 'Last Job Title', value: candidateDetails.candidate.last_job_title || '—' },
              { label: 'Experience (Years)', value: candidateDetails.candidate.experience_years || '—' },
              { label: 'KSA Experience (Years)', value: candidateDetails.candidate.experience_years_at_ksa || '—' },
              { label: 'Last Salary', value: candidateDetails.candidate.last_salary ? `SAR ${Number(candidateDetails.candidate.last_salary).toLocaleString()}` : '—' },
              { label: 'Expected Salary', value: candidateDetails.candidate.expected_salary ? `SAR ${Number(candidateDetails.candidate.expected_salary).toLocaleString()}` : '—' },
              { label: 'Reason for Leaving', value: candidateDetails.candidate.reason_for_leaving || '—' },
              { label: 'Highest Degree', value: candidateDetails.candidate.highest_degree || '—' },
              { label: 'Major', value: candidateDetails.candidate.major || '—' },
              { label: 'Institute', value: candidateDetails.candidate.institute_name || '—' },
              { label: 'Completion Year', value: candidateDetails.candidate.completion_year || '—' },
            ].map((item, idx) => (
              <div key={idx} className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.03)' }}>
                <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>{item.label}</p>
                <p className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{item.value}</p>
              </div>
            ))}
          </div>
        )}

        {moreInfoTab === 'hiringRequest' && candidateDetails.hiringRequests && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: 'Reference Number', value: candidateDetails.hiringRequests.referenceNumber || candidateDetails.hiringRequests.ReferenceNumber || '—' },
              { label: 'Request Type', value: candidateDetails.hiringRequests.request_type || '—' },
              { label: 'Hiring Type', value: candidateDetails.hiringRequests.hiring_Type || '—' },
              { label: 'Position / Grade', value: candidateDetails.hiringRequests.position || '—' },
              { label: 'Role', value: (candidateDetails.hiringRequests.request_type === 'Exceptional' || candidateDetails.hiringRequests.request_type === 'Outsourced') ? (candidateDetails.hiringRequests.role_text || '—') : (candidateDetails.candidate.role || '—') },
              { label: 'Department', value: candidateDetails.candidate.department_name || '—' },
              { label: 'Priority', value: candidateDetails.hiringRequests.priority || '—' },
              { label: 'Position Type', value: candidateDetails.hiringRequests.position_type || '—' },
              { label: 'Job Location', value: candidateDetails.hiringRequests.job_location || '—' },
              { label: 'Status', value: candidateDetails.hiringRequests.status || '—' },
              { label: 'Created By', value: (() => { const u = hiringManagers.find(m => m.username === candidateDetails.hiringRequests.created_by || m.email === candidateDetails.hiringRequests.created_by); return u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : candidateDetails.hiringRequests.created_by || '—'; })() },
              { label: 'Created Date', value: candidateDetails.hiringRequests.date_created ? new Date(candidateDetails.hiringRequests.date_created).toLocaleDateString() : '—' },
              { label: 'Planned Start Date', value: candidateDetails.hiringRequests.planned_start_date ? new Date(candidateDetails.hiringRequests.planned_start_date).toLocaleDateString() : '—' },
            ].map((item, idx) => (
              <div key={idx} className="p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.03)' }}>
                <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>{item.label}</p>
                <p className="text-sm font-semibold" style={{ color: PRIMARY_COLOR }}>{item.value}</p>
              </div>
            ))}
            {candidateDetails.hiringRequests.reason_for_hire && (
              <div className="col-span-2 md:col-span-3 p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.03)' }}>
                <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>Reason for Hire</p>
                <p className="text-sm leading-relaxed" style={{ color: PRIMARY_COLOR }}>{candidateDetails.hiringRequests.reason_for_hire}</p>
              </div>
            )}
            {candidateDetails.hiringRequests.job_description && (
              <div className="col-span-2 md:col-span-3 p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.03)' }}>
                <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>Job Description</p>
                <p className="text-sm leading-relaxed" style={{ color: PRIMARY_COLOR }}>{candidateDetails.hiringRequests.job_description}</p>
              </div>
            )}
            {candidateDetails.hiringRequests.preferred_requirment && (
              <div className="col-span-2 md:col-span-3 p-2.5 rounded-lg" style={{ backgroundColor: 'rgba(18,76,114,0.03)' }}>
                <p className="text-[11px] font-medium" style={{ color: 'rgba(18,76,114,0.5)' }}>Preferred Requirements</p>
                <p className="text-sm leading-relaxed" style={{ color: PRIMARY_COLOR }}>{candidateDetails.hiringRequests.preferred_requirment}</p>
              </div>
            )}

            {/* Approval Flow for Exceptional/Outsourced — same as RequestDetails. Hidden for requests created before production cutoff (2026-04-20). Safari-safe date parsing: replace space with 'T' for ISO compatibility. */}
            {(candidateDetails.hiringRequests.request_type === 'Exceptional' || candidateDetails.hiringRequests.request_type === 'Outsourced') && candidateDetails.hiringRequests.date_created && !isNaN(new Date(String(candidateDetails.hiringRequests.date_created).replace(' ', 'T')).getTime()) && new Date(String(candidateDetails.hiringRequests.date_created).replace(' ', 'T')).getTime() >= new Date('2026-04-20T00:00:00').getTime() && (
              <div className="col-span-2 md:col-span-3 mt-2 flex items-center gap-2 p-3 rounded-lg overflow-x-auto" style={{ backgroundColor: 'rgba(18,76,114,0.03)', border: '1px solid rgba(18,76,114,0.08)' }}>
                {[
                  { role: 'Finance Manager', label: 'Finance', approved: !!candidateDetails.hiringRequests.isFinanceManagerApproved || !!candidateDetails.hiringRequests.IsFinanceManagerApproved, current: !(candidateDetails.hiringRequests.isFinanceManagerApproved || candidateDetails.hiringRequests.IsFinanceManagerApproved) },
                  { role: 'HR Director', label: 'HR Director', approved: !!candidateDetails.hiringRequests.isHRDirectorApproved || !!candidateDetails.hiringRequests.IsHRDirectorApproved, current: !!(candidateDetails.hiringRequests.isFinanceManagerApproved || candidateDetails.hiringRequests.IsFinanceManagerApproved) && !(candidateDetails.hiringRequests.isHRDirectorApproved || candidateDetails.hiringRequests.IsHRDirectorApproved) },
                  { role: 'CEO', label: 'CEO', approved: !!candidateDetails.hiringRequests.isCEOApproved || !!candidateDetails.hiringRequests.IsCEOApproved, current: !!(candidateDetails.hiringRequests.isHRDirectorApproved || candidateDetails.hiringRequests.IsHRDirectorApproved) && !(candidateDetails.hiringRequests.isCEOApproved || candidateDetails.hiringRequests.IsCEOApproved) },
                ].map((step, idx) => {
                  const approver = step.approved
                    ? (hrAuditTrail || []).find(a =>
                        (a.change_by && a.change_by.toLowerCase().includes(step.role.toLowerCase())) ||
                        (a.logDescription && a.logDescription.toLowerCase().includes(step.role.toLowerCase()))
                      )
                    : null;
                  const color = step.approved ? '#059669' : step.current ? '#d97706' : '#9ca3af';
                  return (
                    <React.Fragment key={idx}>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: color, color: '#fff' }}>
                          {step.approved ? '✓' : idx + 1}
                        </div>
                        <div>
                          <div className="text-xs font-semibold whitespace-nowrap" style={{ color }}>{step.label}</div>
                          <div className="text-[10px] whitespace-nowrap" style={{ color: step.approved ? '#059669' : '#6b7280' }}>
                            {step.approved
                              ? `${approver?.change_by || 'Approved'}${approver?.change_date ? ' · ' + new Date(approver.change_date).toLocaleDateString() : ''}`
                              : step.current ? '⏳ Pending' : 'Waiting'}
                          </div>
                        </div>
                      </div>
                      {idx < 2 && (
                        <div className="flex-shrink-0 w-8 h-0.5 rounded" style={{ backgroundColor: step.approved ? '#059669' : '#e5e7eb' }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end px-5 py-3 border-t flex-shrink-0" style={{ borderColor: 'rgba(18,76,114,0.08)' }}>
        <button onClick={() => setShowMoreInfoPopup(false)} className="px-4 py-2 text-sm bg-white border rounded-lg font-medium" style={{ color: PRIMARY_COLOR, borderColor: 'rgba(18,76,114,0.2)' }}>Close</button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default CandidateDetails;